<template>
  <div v-if="!admin">
    <header id="header-wrapper" :class="[{ loggedOut: !admin && !currentUser }]">
      <div class="container">
        <div class="row align-items-start" style="position:relative">
          <div class="col-lg-2 col-md-2 col-12 logo-cont">
            <router-link id="logo" :to="currentUser ? '/account' : '/'">
              <!-- Light logo (dark "Service Portal" text) for the normal
                   white header; dark-mode logo (white "Service Portal"
                   text) swapped in via CSS on html.dark-mode. The dark
                   logo lives in /public so a missing file won't break the
                   build (it just 404s until the asset is dropped in). -->
              <img class="site-logo site-logo-light"
                   src="@/assets/img/jcsteeleservice-brushed-logo.png"
                   alt="JC Steele Service Customer Portal - Home" />
              <img class="site-logo site-logo-dark"
                   src="/jcsteeleservice-brushed-logo%20-%20dark%20mode.png"
                   alt="JC Steele Service Customer Portal - Home" />
            </router-link>
          </div>

          <!-- Contact info — centered in the column that used to be
               the search bar's spacer. Phone on top, parts email
               directly underneath. The phone is a tel: link (tap to
               dial); the parts email opens the Contact Us modal (the
               same form the mobile contact icon uses) rather than a
               mailto: hand-off, so users get the guided inquiry form. -->
          <div class="col-lg-5 col-md-5 col-12 search-cont contact-cont">
            <div class="header-contact" v-if="currentUser">
              <a class="header-contact-line" :href="'tel:' + companyPhone">
                {{ companyPhone }}
              </a>
              <a class="header-contact-line header-contact-email"
                 href="#" @click.prevent="openContactModal"
                 title="Contact our parts team">
                parts@jcsteele.com
              </a>
            </div>
            <!-- Mobile-only contact icon — replaces the phone/email text on
                 phones. Tapping opens the contact modal (tap-to-call phone +
                 the Email-the-Parts-Team form). Hidden on desktop via CSS. -->
            <button v-if="currentUser" type="button" class="header-contact-icon"
                    @click="openContactModal" aria-label="Contact us"
                    title="Contact us"></button>
          </div>

          <div class="col-lg-5 col-md-5 col-12 account-cont"
               :class="{ 'has-role-line': isRealAdmin || showReturnHome }">
            <!-- Greeting + company selector grouped together. The wrapper
                 is right-anchored to the column edge but auto-sizes to
                 the widest child (the company name), so both lines share
                 the same left edge. -->
            <div class="account-identity" v-if="currentUser">
              <!-- User greeting — first line. Shows "Hi {firstName}"
                   when a first name is on the user record; falls back to
                   email username otherwise. -->
              <div class="user-greeting">
                Hello <span class="user-greeting-name">{{ userGreetingName }}</span>
              </div>

              <!-- Company selector — second line. -->
              <div
                v-if="companies.length > 0"
                class="company-selector-wrapper company-selector-top"
              >
                <div
                  class="company-selector"
                  :class="{ clickable: companies.length > 0 && !companyLocked, 'is-locked': companyLocked }"
                  :title="companyLocked ? 'Locked to JC Steele & Sons — no territory customers are assigned to you yet. A Master can assign territories in Account Setup → Sales Territories.' : ''"
                  @click="!companyLocked && companies.length > 0 && toggleCompanyDropdown()"
                  v-click-outside="closeCompanyDropdown"
                >
                  <span class="company-selector-label">{{ selectedCompanyName }}</span>
                  <span v-if="companyLocked" class="company-selector-lock" style="margin-left:6px;font-size:11px;opacity:.75" aria-hidden="true">🔒</span>
                  <span
                    v-else-if="companies.length > 1"
                    class="company-selector-arrow"
                    :class="{ open: showCompanyDropdown }"
                  >
                    &#9662;
                  </span>
                </div>

                <div v-if="showCompanyDropdown" class="company-dropdown">
                  <!-- First line = the company already selected. On mobile the
                       trigger is icon-only, so this confirms the current pick
                       at the top of the menu. (Hidden on desktop via CSS, where
                       the name shows inline in the selector itself.) -->
                  <div class="company-dropdown-selected">{{ selectedCompanyName }}</div>
                  <!-- Search only matters when there's more than one company. -->
                  <div class="company-search-wrapper" v-if="companies.length > 1">
                    <div class="company-search-field">
                      <svg class="company-search-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" width="13" height="13" aria-hidden="true">
                        <path fill="currentColor" d="M505 442.7L405.3 343c-4.5-4.5-10.6-7-17-7H372c27.6-35.3 44-79.7 44-128C416 93.1 322.9 0 208 0S0 93.1 0 208s93.1 208 208 208c48.3 0 92.7-16.4 128-44v16.3c0 6.4 2.5 12.5 7 17l99.7 99.7c9.4 9.4 24.6 9.4 33.9 0l28.3-28.3c9.4-9.4 9.4-24.6.1-34zM208 336c-70.7 0-128-57.2-128-128 0-70.7 57.2-128 128-128 70.7 0 128 57.2 128 128 0 70.7-57.2 128-128 128z"/>
                      </svg>
                      <input
                        type="text"
                        v-model="companySearchTerm"
                        placeholder="Type to search..."
                        class="company-search-input"
                        ref="companySearchInput"
                        @click.stop
                      />
                    </div>
                    <label
                      v-if="hasClosedCompanies"
                      class="company-closed-toggle"
                      @click.stop
                    >
                      <input type="checkbox" v-model="showClosedCompanies" />
                      <span>Show closed</span>
                    </label>
                  </div>

                  <div
                    v-for="company in filteredCompanies"
                    :key="company.value"
                    class="company-dropdown-option"
                    :class="{ active: company.value == selectedCompanyId }"
                    @mousedown="selectCompany(company)"
                  >
                    {{ company.text }}
                  </div>

                  <div
                    v-if="filteredCompanies.length === 0"
                    class="company-dropdown-empty"
                  >
                    No matches found
                  </div>
                </div>
              </div>

              <!-- Sales-rep row — mirrors the admin Role/User/Back-to-view row (same .header-role-line format) so
                   the header stays compact and My Account stays visible (via .has-role-line on the container).
                   Role is READ-ONLY (a rep can't preview roles); the User dropdown lists the CURRENT customer's
                   contacts (via the rep-allowed /user/company-contacts endpoint) and picking one prefills the
                   "Request Parts" contact. Shows only for a non-admin rep currently on a customer (non-NC999). -->
              <div v-if="showReturnHome && !isRealAdmin" class="header-role-line is-preview">
                <span class="header-role-label">Role:</span>
                <span class="header-role-static">{{ effectiveRoleName }}</span>
                <template v-if="repContacts.length">
                  <span class="header-role-label" style="margin-left:10px;">User:</span>
                  <select class="header-role-select" v-model="repContactSel" @change="onRepContactPick"
                          title="Who at this customer wants the parts — prefills the Request Parts contact">
                    <option value="">— select contact —</option>
                    <option v-for="u in repContacts" :key="u.id" :value="u.id">{{ u.label }}</option>
                  </select>
                </template>
                <button type="button" class="header-role-switch"
                        @click="repReturnHome"
                        title="Return to your JC Steele (NC999) home view">
                  &#8592; Back to my view
                </button>
              </div>

              <!-- Role line — ADMIN ONLY (gated on the REAL signed-in
                   identity, so it stays visible even while previewing another
                   role). A dropdown of the roles defined in the database lets
                   an admin preview the portal exactly as any role sees it,
                   then switch back by re-selecting their own role. Regular
                   customers never see this row. -->
              <div v-if="isRealAdmin" class="header-role-line"
                   :class="{ 'is-preview': viewingAsRole || viewingAsUser }">
                <span class="header-role-label">Role:</span>
                <select class="header-role-select"
                        :value="effectiveRoleName"
                        @change="onRoleChange($event.target.value)"
                        title="Preview the portal as another role (admin only)">
                  <option v-for="r in roleOptions" :key="r" :value="r">{{ r }}</option>
                </select>
                <!-- View as a SPECIFIC user of the selected company (admin only) — adopts their role + their personal report layouts. -->
                <span v-if="companyUsers.length" class="header-role-label" style="margin-left:10px;">User:</span>
                <select v-if="companyUsers.length" class="header-role-select"
                        :value="viewingAsUser ? viewingAsUser.id : ''"
                        @change="onUserChange($event.target.value)"
                        title="Preview the report as a specific user of this company (admin only)">
                  <option value="">— my view —</option>
                  <option v-for="u in companyUsers" :key="u.id" :value="u.id">{{ userLabel(u) }}</option>
                </select>
                <button v-if="viewingAsRole || viewingAsUser" type="button" class="header-role-switch"
                        @click="resetRole"
                        title="Return to your administrator view">
                  Back to my view
                </button>
              </div>
            </div>

            <!-- Utility row: My Account on the right.
                 The "View Cart" cluster (cart icon + Orders / Quotes
                 links + count badges) was previously the left half
                 of this row. It was removed because the centralized
                 CartWidget on each page (with its always-visible
                 mid-right side pill) now owns cart navigation +
                 count display app-wide. The cart-expiry timer logic
                 below in this file is unrelated to the badges and
                 stays as-is. -->
            <div class="row header-utility-row">
              <div id="header-account" class="nav col-lg-12">
                <div v-if="currentUser" class="nav-item dropdown">
                  <a
                    class="nav-link dropdown-toggle"
                    data-toggle="dropdown"
                    href="#"
                    role="button"
                    aria-haspopup="true"
                    aria-expanded="false"
                  >
                    <span>My Account</span>
                  </a>

                  <div class="dropdown-menu">
                    <a class="dropdown-item" href="/account/information">Account Information</a>

                    <!-- Admin Dashboard opens the Account Setup panel (the same
                         view as the SETUP side-widget pill). It emits a root
                         event the AccountSetupWidget listens for and runs its
                         openModal() (which loads the setup data), rather than
                         linking to the old /admin console. -->
                    <div v-if="currentUser.admin">
                      <div class="dropdown-divider"></div>
                      <a class="dropdown-item" href="#"
                         @click.prevent="$root.$emit('open-account-setup')">Admin Dashboard</a>
                    </div>

                    <div class="dropdown-divider"></div>
                    <a class="dropdown-item" href="#" @click.prevent="handleLogout">
                      Logout
                    </a>
                  </div>
                </div>

                <div v-else class="nav-item">
                  <!-- Login link styled to mirror the My Account
                       dropdown trigger when a user IS logged in.
                       Same red person-silhouette SVG icon, same
                       size/positioning. The icon comes from
                       the .nav-link-with-icon ::before rule
                       below, which copies the exact SVG data
                       URI used by .dropdown-toggle::before in
                       the global stylesheet so the two states
                       are visually consistent. -->
                  <a class="nav-link nav-link-with-icon"
                     href="/login"><span>Login</span></a>
                </div>
              </div>
            </div>

            <div class="header-tools">
              <div
                align="right"
                class="fsElementContent"
                id="fsEl_1469"
                data-use-new="true"
              ></div>
              <div align="right" class="fsElementContent"></div>
              <!-- Google Translate moved to a floating toggle at the bottom-left
                   of the viewport (see #google-translate-floating below) so it
                   stays reachable without taking header space. -->
            </div>
          </div>
        </div>
      </div>
    </header>

    <!-- ===== CONTACT MODAL =====
         Opened by the mobile header's contact icon. Shows the phone number
         (tap-to-call) first, then the "Email the Parts Team" form (same fields
         + same endpoint as the Search page's Contact-us form). -->
    <b-modal id="header-contact-modal"
             v-model="showContactModal"
             static
             :title="isSalesRequester ? 'Request Parts for a Customer' : 'Contact JC Steele Parts'"
             size="lg"
             modal-class="hc-contact-modal"
             :ok-title="contactSending ? 'Sending…' : (isSalesRequester ? 'Send Request to Parts Team' : 'Email Inquiry or call 1-800-278-3353')"
             ok-variant="primary"
             cancel-title="Cancel"
             cancel-variant="outline-secondary"
             :ok-disabled="contactSending || !contactFormValid"
             @ok="submitPartsInquiry"
             @hidden="resetContactForm">

      <!-- Phone first — a big tap-to-call button (dials on a phone). -->
      <a class="hc-modal-phone" href="tel:18002783353">
        <span class="hc-modal-phone-ico" aria-hidden="true"></span>
        <span class="hc-modal-phone-text">
          <span class="hc-modal-phone-num">(800) 278-3353</span>
          <span class="hc-modal-phone-sub">Tap to call our parts team</span>
        </span>
      </a>

      <div class="hc-modal-or"><span>{{ isSalesRequester ? 'or send a request to the parts team' : 'or send us a message' }}</span></div>

      <!-- Sales-rep flow: this form emails Parts on behalf of a customer. Make it
           unmistakable that the reply routes to the REP, not the customer. -->
      <div v-if="isSalesRequester && !contactSuccess" class="hc-rep-note"
           style="margin:0 0 14px;padding:10px 14px;background:#fff7f8;border:1px solid #e6c3c9;border-left:4px solid #9e2436;border-radius:6px;font-size:13px;color:#333;line-height:1.5;">
        You're requesting parts <strong>on behalf of your customer</strong>. The parts team will reply to
        <strong>you</strong> ({{ contactForm.customerEmail || 'your email' }}) — not the customer.
      </div>

      <div v-if="contactSuccess" class="ui visible success message">
        <div class="header">Inquiry sent.</div>
        <p>Our parts team will follow up by email shortly.</p>
      </div>
      <div v-if="contactError" class="ui visible error message">
        <div class="header">Could not send your inquiry.</div>
        <p>{{ contactError }}</p>
      </div>

      <div v-if="!contactSuccess" class="parts-inquiry-form">
        <div class="form-row">
          <div class="col-md-6">
            <label for="hcName">{{ isSalesRequester ? 'Your Name (Sales Rep)' : 'Your Name' }} <span class="text-danger">*</span></label>
            <input id="hcName" type="text" class="form-control" maxlength="120"
                   v-model="contactForm.customerName" />
          </div>
          <div class="col-md-6">
            <label for="hcEmail">{{ isSalesRequester ? 'Your Reply Email' : 'Reply Email' }} <span class="text-danger">*</span></label>
            <input id="hcEmail" type="email" class="form-control" maxlength="200"
                   v-model="contactForm.customerEmail" />
          </div>
        </div>
        <div class="form-row mt-3">
          <div class="col-md-6">
            <label for="hcCompany">{{ isSalesRequester ? 'Customer / Company' : 'Company' }}<span v-if="isSalesRequester" class="text-danger"> *</span></label>
            <input id="hcCompany" type="text" class="form-control" maxlength="200"
                   :placeholder="isSalesRequester ? 'e.g. Belden Brick Co.' : ''"
                   v-model="contactForm.customerCompany" />
          </div>
          <div class="col-md-6">
            <label for="hcMachine">Machine / Equipment Description</label>
            <input id="hcMachine" type="text" class="form-control" maxlength="200"
                   placeholder="e.g. 90A Extruder, serial 12345"
                   v-model="contactForm.machineDescription" />
          </div>
        </div>
        <!-- Sales-rep only: pick a known portal contact at this customer to fill the fields below.
             Only shows when the customer actually has portal users; the fields stay editable regardless. -->
        <div v-if="isSalesRequester && contactUsers.length" class="form-row mt-3">
          <div class="col-12">
            <label for="hcContactPick">Known contacts at this customer</label>
            <select id="hcContactPick" class="form-control" v-model="contactUserSel" @change="onContactUserPick">
              <option value="">— choose a contact, or type below —</option>
              <option v-for="u in contactUsers" :key="u.id" :value="u.id">{{ u.label }}</option>
            </select>
            <small class="form-text text-muted">Picking one fills the name + email. Not listed? Just type them.</small>
          </div>
        </div>
        <!-- Sales-rep only: the END customer's on-site contact the parts are for.
             Presence of Contact Name drives the "request on behalf of a customer"
             email layout server-side (PartsInquiry.hbs branches on ContactName). -->
        <div v-if="isSalesRequester" class="form-row mt-3">
          <div class="col-md-6">
            <label for="hcContactName">Customer Contact Name <span class="text-danger">*</span></label>
            <input id="hcContactName" type="text" class="form-control" maxlength="120"
                   placeholder="Who at the customer needs the parts? e.g. Paul Runyon"
                   v-model="contactForm.contactName" />
          </div>
          <div class="col-md-6">
            <label for="hcContactEmail">Customer Contact Email</label>
            <input id="hcContactEmail" type="email" class="form-control" maxlength="200"
                   placeholder="optional — e.g. paul.runyon@beldenbrick.com"
                   v-model="contactForm.contactEmail" />
          </div>
        </div>
        <!-- Sales-rep only: quantity needed, prefilled with the average past-order quantity for the part. -->
        <div v-if="isSalesRequester" class="form-row mt-3">
          <div class="col-md-4">
            <label for="hcQty">Quantity</label>
            <input id="hcQty" type="number" min="1" step="1" class="form-control"
                   placeholder="e.g. 2"
                   v-model="contactForm.quantity" />
            <small class="form-text text-muted">Prefilled with the average per past order — adjust as needed.</small>
          </div>
        </div>
        <div class="form-row mt-3">
          <div class="col-12">
            <label for="hcDesc">{{ isSalesRequester ? 'What does your customer need?' : 'What can we help you with?' }} <span class="text-danger">*</span></label>
            <textarea id="hcDesc" class="form-control" rows="5" maxlength="2000"
                      :placeholder="isSalesRequester ? 'Describe the parts your customer needs — part numbers, machine, quantity…' : 'Tell us what parts you need or what is going on with your equipment.'"
                      v-model="contactForm.description"></textarea>
          </div>
        </div>
        <div class="form-row mt-3">
          <div class="col-12">
            <label for="hcFile">Attach a photo (optional)</label>
            <input id="hcFile" type="file" ref="contactFile" accept=".jpg,.jpeg,.png"
                   @change="onContactFileChange" />
            <div v-if="contactFileError" class="text-danger mt-1">{{ contactFileError }}</div>
            <small class="form-text text-muted">
              JPG or PNG, max 1 MB. Helpful for nameplates / serial tags.
            </small>
          </div>
        </div>
      </div>
    </b-modal>

    <!-- Floating search bar overlaid on the breadcrumb row.
         Position: fixed so it sits at a known offset from the top of the
         viewport regardless of how tall the sticky header reports. The
         App.vue dynamic body padding-top moves the breadcrumb down to
         clear the header, and we mirror that offset here so this search
         lands ON the breadcrumb row, right-aligned.

         Auth-gated: only renders for logged-in users. The search API
         requires the JWT to return company-specific results, so the bar
         is dead weight for anonymous visitors on /login, /registration,
         and the landing page; hiding it also gives those pages a
         cleaner top-right area free of unactionable controls. -->
    <!-- Product search — every page EXCEPT Sales Insights (/reports). The report
         owns its date range + filters via its own in-page filter bar, so the
         header breadcrumb-row date shortcuts that used to live here were retired
         and the breadcrumb row itself is hidden on that route (see App.vue
         showGlobalBreadcrumbs) to pull the report cards up under the nav. -->
    <div id="header-search" v-if="currentUser && !isReportRoute">
      <form id="search" @submit.prevent="search">
        <div class="search-input-container">
          <button type="submit" class="search-btn" :disabled="disabled">
            <span></span>
          </button>

          <input
            type="text"
            v-model="searchTerm"
            placeholder="Search"
            id="input-with-list"
            autocomplete="off"
            list=""
            @focus="showCustomDropdown = true"
            @blur="onInputBlur"
          />

          <!-- Clear (×) — wipes the search field + the page's header-search and
               its remembered term. Only shown when there's something to clear. -->
          <button
            v-if="searchTerm"
            type="button"
            class="search-clear-btn"
            @click="clearSearch"
            aria-label="Clear search"
            title="Clear search"
          >&times;</button>

          <div
            v-if="showCustomDropdown && partialTerms.length > 0 && searchTerm.length >= 3"
            class="custom-dropdown"
          >
            <div
              v-for="option in partialTerms"
              :key="option.key"
              class="dropdown-option"
              @mousedown="selectOption(option)"
            >
              <span class="option-code">{{ option.value }}</span>
              <span
                v-if="option.assembly || option.machineName"
                class="option-detail"
              >
                <span v-if="option.assembly">{{ option.assembly }}</span>
                <span v-if="option.assembly && option.machineName"> · </span>
                <span v-if="option.machineName">{{ option.machineName }}</span>
              </span>
            </div>
          </div>
        </div>

        <span v-if="msg.isTrue" class="text-danger message">
          {{ msg.term }}
        </span>
        <span v-else class="text-white">{{ msg.term }}</span>
      </form>
    </div>
    <!-- Saving Cart Overlay -->
    <div v-if="showSavingOverlay" class="saving-overlay">
      <div class="saving-message">
        <div class="saving-spinner"></div>
        <p>Saving your cart...</p>
      </div>
    </div>

    <!-- Test Mode toggle.
         Master-only, visible only when a test-eligible
         company is selected (currently NC999 / id 237 —
         see canSeeTestMode computed for the gate).
         Position: fixed, centered horizontally, overlaid
         on the breadcrumb row using the same vertical
         offset pattern as #header-search above.
         When the toggle is ON, the SQL trigger
         trg_Orders_BlockTestCompanyVisualSync intercepts
         INSERTs to dbo.Orders for this company and writes
         sentinel values (VisualSouthOrderId='999',
         DateExported=now) so OrderSync.cs's scheduled
         function skips the order on its next run. When
         OFF, orders sync normally. The button calls
         /Company/{id}/test-mode (Master-only) which
         flips Companies.IsTestModeActive. -->
    <div id="test-mode-toggle-row" v-if="canSeeTestMode">
      <button
        type="button"
        class="test-mode-toggle-btn"
        :class="{ active: testModeActive }"
        :disabled="testModeBusy"
        @click="toggleTestMode">
        <span class="test-mode-dot"></span>
        <span class="test-mode-label">
          Test Mode: {{ testModeActive ? 'ON' : 'OFF' }}
        </span>
      </button>
    </div>

    <!-- Floating font-size toggle.
         Sits at the bottom-right of the viewport, stacked above the
         Google Translate widget so the two floating affordances form
         a coherent set in the same corner. Cycles NORMAL → MEDIUM →
         LARGE → NORMAL on click; current size is mirrored to
         localStorage.fontSize and applied as html.font-size-{level}
         (matches the existing CSS rules in steele-consistency-fix.css
         at section 9 "CUSTOMER FONT SIZE TOGGLE").

         Collapsible the same way as the Translate widget below: the
         × hides the label, leaving only the Aa pill, and the user's
         choice persists in localStorage.fontToggleCollapsed.

         IMPORTANT: this duplicates the existing #font-size-toggle
         that's likely rendered from App.vue (or wherever it currently
         lives globally). Remove that older instance — having two
         elements with the same id will cause double-click-cycle and
         CSS conflicts. -->
    <!-- ===== FLOATING UTILITY TOGGLES =====
         Three stacked toggles in the bottom-right corner: bug
         report, font size, language. All collapsed by default
         (small pill showing only the icon); clicking a pill
         expands it to show its content with × on the LEFT for
         collapse. Vertical spacing is generous so users can hit
         the right one without overlap.

         z-index: 10000 puts them above the right rail (which
         lives at z-index: 1100 alongside the sticky header). -->
    <!-- Rendered into App.vue's <portal-target name="util-toggles"> so
         these toggles escape the .stickyHeader stacking context
         (z 1100) and live as siblings of #app — guaranteeing they
         paint above the side rails (z 999) regardless of any
         ancestor stacking-context shenanigans. -->
    <portal to="util-toggles">
    <div id="utility-toggle-stack">
      <!-- Color mode — single button that TOGGLES light ⇄ dark. The icon shows
           the CURRENT mode: sun (light), moon (dark). Persisted in
           localStorage.colorMode and applied as the `dark-mode` class on <html>
           (preloaded in main.js to avoid a flash, themed by darkmode.css).
           MID-DARK RETIRED 2026-07-26: it is no longer selectable here, but
           mid-mode.css stays on disk + imported (inert) and the `mid-mode`
           class still themes correctly if something applies it (the admin debug
           overlay's CSS-file preview). Anyone stored as "mid" is migrated to
           dark on their next load — see main.js. Available to every user type
           on every page via the util-toggles portal. -->
      <button
        type="button"
        class="util-toggle util-toggle-dark"
        :class="{ 'is-on': colorMode !== 'light' }"
        @click="cycleColorMode"
        :aria-label="'Color mode: ' + colorMode + ' — click to change'"
        :title="colorMode === 'light' ? 'Light mode — click for Dark' : 'Dark mode — click for Light'"
      >
        <!-- Light → sun -->
        <svg v-if="colorMode === 'light'" class="util-icon" xmlns="http://www.w3.org/2000/svg"
             viewBox="0 0 24 24" fill="none" stroke="currentColor"
             stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
             aria-hidden="true">
          <circle cx="12" cy="12" r="5"/>
          <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/>
        </svg>
        <!-- Dark (and any legacy mid) → moon -->
        <svg v-else class="util-icon" xmlns="http://www.w3.org/2000/svg"
             viewBox="0 0 24 24" fill="none" stroke="currentColor"
             stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
             aria-hidden="true">
          <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
        </svg>
      </button>

      <!-- Report a Bug — opens the /report-bug page directly,
           no expanded state needed. The icon is a simple bug
           shape rendered as inline SVG. -->
      <button
        v-if="currentUser"
        type="button"
        class="util-toggle util-toggle-bug"
        :style="(canToggleDebug && debugOverlayOn) ? 'box-shadow:0 0 0 2px #1f9d55;' : ''"
        @click="onBugButtonClick"
        :aria-label="canToggleDebug ? 'Toggle layout debug overlay' : 'Report a bug'"
        :title="canToggleDebug ? (debugOverlayOn ? 'Layout debug overlay: ON — click to turn off' : 'Layout debug overlay: OFF — click to turn on') : 'Report a bug'"
      >
        <svg class="util-icon" xmlns="http://www.w3.org/2000/svg"
             viewBox="0 0 24 24" fill="none" stroke="currentColor"
             stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
             aria-hidden="true">
          <rect x="8" y="6" width="8" height="14" rx="4"/>
          <path d="M19 7l-3 2"/>
          <path d="M5 7l3 2"/>
          <path d="M19 13h-3"/>
          <path d="M8 13H5"/>
          <path d="M19 19l-3-2"/>
          <path d="M5 19l3-2"/>
          <path d="M9 6V4a3 3 0 1 1 6 0v2"/>
        </svg>
      </button>

      <!-- Font size — collapsed shows Aa pill, expanded shows
           × on left + Aa icon + size label. Click on the body
           cycles size; click on × collapses. -->
      <div class="util-toggle util-toggle-font"
           :class="{ 'is-collapsed': fontToggleCollapsed }"
           @click="!fontToggleCollapsed || toggleFontToggleCollapsed()">
        <button
          v-if="!fontToggleCollapsed"
          type="button"
          class="util-close"
          @click.stop="toggleFontToggleCollapsed"
          aria-label="Hide font size selector"
          title="Hide"
        >×</button>
        <button
          type="button"
          class="util-body"
          @click.stop="fontToggleCollapsed
            ? toggleFontToggleCollapsed()
            : cycleFontSize()"
          :aria-label="fontToggleCollapsed
            ? 'Show font size selector'
            : 'Font size: ' + fontSize + '. Click to change.'"
        >
          <span class="util-icon util-icon-text" aria-hidden="true">Aa</span>
          <span v-if="!fontToggleCollapsed" class="util-label">{{ fontSize }}</span>
        </button>
      </div>

      <!-- Language / translate — collapsed shows globe pill,
           expanded shows × on left + globe + Google's select. -->
      <div class="util-toggle util-toggle-translate"
           :class="{ 'is-collapsed': translateCollapsed }">
        <button
          v-if="!translateCollapsed"
          type="button"
          class="util-close"
          @click.stop="toggleTranslateCollapsed"
          aria-label="Hide language selector"
          title="Hide"
        >×</button>
        <button
          v-if="translateCollapsed"
          type="button"
          class="util-body"
          @click.stop="toggleTranslateCollapsed"
          aria-label="Show language selector"
          title="Show language selector"
        >
          <span class="util-icon" aria-hidden="true">🌐</span>
        </button>
        <div v-show="!translateCollapsed" class="util-translate-content">
          <span class="util-icon" aria-hidden="true">🌐</span>
          <div id="google_translate_element"></div>
        </div>
      </div>
    </div>
    </portal>
  </div>

  <div v-else>
    <section v-if="error">
      <div class="container">
        <div class="ui error message">
          <p>
            We're sorry, we're not able to retrieve this information at the
            moment, please try back later
          </p>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import Vue from "vue";
import { recordSearch } from "@/assets/js/activity";
import axios from "axios";
import { mapGetters } from "vuex";
import APIService from "@/assets/js/APIService";
import FeapiService from "../assets/js/FeApiService";
import FilterSearch from "./Views/Reports/FilterSearch";
const apiService = new APIService();
const feapiService = new FeapiService();

export default {
  name: "Head",
  components: { FilterSearch },
  directives: {
    'click-outside': {
      bind: function (el, binding) {
        el.__clickOutsideHandler = function (event) {
          if (!(el === event.target || el.contains(event.target))) {
            binding.value(event);
          }
        };
        document.addEventListener('click', el.__clickOutsideHandler);
      },
      unbind: function (el) {
        document.removeEventListener('click', el.__clickOutsideHandler);
      }
    }
  },
  computed: {
    ...mapGetters({
      currentUser: "currentUser",
      isRealAdmin: "isRealAdmin",
      realRoleName: "realRoleName",
      effectiveRoleName: "effectiveRoleName",
      viewingAsRole: "viewingAsRole",
      viewingAsUser: "viewingAsUser"
    }),
    // Non-customer roles use the header search to drive the Customer Insights
    // report; the Customer role keeps the product search.
    isReportingRole() {
      return !!this.currentUser && this.effectiveRoleName !== "Customer";
    },
    // Sales-rep "request parts for my customer" flow. Master / Sales / Sales All
    // reps use the contact modal to email Parts ON BEHALF of a customer (reply
    // routes to the rep). The Customer role keeps the original self-service form.
    // Preview-aware via effectiveRoleName so "view as role" reflects it.
    isSalesRequester() {
      var r = this.effectiveRoleName;
      return r === "Master" || r === "Sales" || r === "Sales All";
    },
    // Show a "Back to my view" (return to NC999) button when a rep has switched INTO a customer company (via
    // Email Insights / the report) and might want to bail without placing anything. Non-Customer, not while an
    // admin is previewing a role/user (they use their own "Back to my view"), and only when off NC999.
    showReturnHome() {
      if (!this.currentUser || this.effectiveRoleName === "Customer") return false;
      if (this.viewingAsRole || this.viewingAsUser) return false;
      var self = this;
      var sel = (this.companies || []).find(function (c) { return String(c.value) === String(self.selectedCompanyId); });
      var name = sel ? (sel.text || "") : "";
      return !!name && !/\(NC999\)/.test(name);
    },
    // Sales Insights report page (route-based, role-agnostic). On this page the header
    // shows the date-range shortcuts INSTEAD of the product search; on every other page
    // the product search shows for all roles.
    isReportRoute() {
      // Any page under /reports/ (the Sales Insights report AND the Reports dashboard) hides the product
      // search — those pages own their own filters or have nothing to search. Prefix match, not exact.
      return !!this.$route && ((this.$route.path || "").indexOf("/reports") === 0);
    },
    // Today as YYYY-MM-DD — caps the header From/To pickers so no future date can be selected.
    todayIso() {
      return new Date().toISOString().slice(0, 10);
    },
    // Elevated/staff users (admin or Master) get the layout DEBUG OVERLAY
    // toggle on the bug button; everyone else gets "Report a bug" (/report-bug).
    canToggleDebug() {
      var u = this.currentUser || {};
      return !!(u.admin || u.master || u.Master);
    },
    // Reactive admin-route detection. Reads the current
    // route's `props.default.admin` flag. Reactive to
    // $route changes (computed properties auto-track
    // dependencies) so the header correctly shows/hides
    // when the user navigates between admin and customer
    // routes via router-link. Replaces a stale data-field
    // approach that only ran once in mounted() and left
    // the header hidden after navigating away from /admin.
    admin() {
      return !!(
        this.$route &&
        this.$route.matched &&
        this.$route.matched.length > 0 &&
        this.$route.matched[0].props &&
        this.$route.matched[0].props.default &&
        this.$route.matched[0].props.default.admin
      );
    },

    // Gate for showing the Test Mode toggle. Three layers:
    // - Master role (the backend endpoint is Master-only;
    //   we mirror that on the frontend so non-Masters
    //   never see a control they can't actuate).
    // - Selected company is in the test-eligibility
    //   allowlist (currently just NC999 / id 237 —
    //   matches the same allowlist on
    //   CompanyController.ToggleTestMode).
    // - Company state is loaded (selectedCompanyId !== '').
    //
    // If the allowlist grows past 2-3 companies, move it
    // to a backend-driven property on the Company response
    // so the frontend stops needing to know specific ids.
    canSeeTestMode() {
      var TEST_ELIGIBLE_COMPANY_IDS = [237];
      if (!this.currentUser) return false;
      if (!this.currentUser.master && !this.currentUser.Master) {
        return false;
      }
      var cid = parseInt(this.selectedCompanyId, 10);
      if (!cid) return false;
      return TEST_ELIGIBLE_COMPANY_IDS.indexOf(cid) !== -1;
    },
    // Friendly display name for the header greeting. Prefers firstName,
    // falls back to the local part of email, then to a generic label
    // so the greeting never renders as "Hi, undefined".
    userGreetingName() {
      var u = this.currentUser || {};
      if (u.firstName) return u.firstName;
      if (u.FirstName) return u.FirstName;
      if (u.first_name) return u.first_name;
      if (u.email && u.email.indexOf("@") > 0) {
        return u.email.substring(0, u.email.indexOf("@"));
      }
      return "there";
    },
    // Contact modal: require a name, a valid email, and a message.
    contactFormValid() {
      var p = this.contactForm;
      if (!p.customerName || !p.customerName.trim()) return false;
      if (!p.customerEmail || !p.customerEmail.trim()) return false;
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(p.customerEmail)) return false;
      if (!p.description || !p.description.trim()) return false;
      // Sales-rep request: the customer company + the customer contact name are
      // required (they define who/where the parts are for and trigger the
      // rep-request email layout). Contact email is optional but must be valid.
      if (this.isSalesRequester) {
        if (!p.customerCompany || !p.customerCompany.trim()) return false;
        if (!p.contactName || !p.contactName.trim()) return false;
        if (p.contactEmail && p.contactEmail.trim() &&
            !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(p.contactEmail)) return false;
      }
      return true;
    },
    headerImageSrc() {
      if (this.companyLogoFileName == "") {
        return "";
      }
      return `${require(`@/assets/${this.companyLogoFileName}`)}`;
    },
    partialTerms: function () {
      return this.searchOption;
    },
    companyLocked: function () {
      // Sales reps WITH territory customers may switch company from the header (user 2026-08-01) —
      // the list is territory-scoped server-side (GetCompanySelectItems: NC999 + the customers in
      // their rpt_UserTerritory territories), so they can only ever pick their own book of business.
      // Home company stays NC999 (session homing in getCompanies). A rep with NO territory data gets
      // only NC999 back → keep the lock: there's nothing to switch to, and the tooltip explains why.
      return this.effectiveRoleName === 'Sales' && (this.companies || []).length <= 1;
    },
    selectedCompanyName: function () {
      if (this.selectedCompanyId && this.companies.length > 0) {
        var match = this.companies.find(c => c.value == this.selectedCompanyId);
        return match ? match.text : 'Select Company';
      }
      return 'Select Company';
    },
    filteredCompanies: function () {
      var list = this.companies;
      if (!this.showClosedCompanies) {
        list = list.filter(function (c) {
          var upper = c.text.toUpperCase();
          return upper.indexOf('-CLOSED-') === -1 &&
                 upper.indexOf('DO NOT USE') === -1 &&
                 upper.indexOf('NO LONGER') === -1;
        });
      }
      if (this.companySearchTerm) {
        var term = this.companySearchTerm.toLowerCase();
        list = list.filter(function (c) {
          return c.text.toLowerCase().indexOf(term) !== -1;
        });
      }
      return list;
    },
    hasClosedCompanies: function () {
      return this.companies.some(function (c) {
        var upper = c.text.toUpperCase();
        return upper.indexOf('-CLOSED-') !== -1 ||
               upper.indexOf('DO NOT USE') !== -1 ||
               upper.indexOf('NO LONGER') !== -1;
      });
    },
    expiryCountdownMinutes: function () {
      return Math.floor(this.expiryCountdownSeconds / 60);
    },
    expiryCountdownSecondsFormatted: function () {
      var secs = this.expiryCountdownSeconds % 60;
      return secs < 10 ? '0' + secs : secs;
    }
  },
  mounted() {
    this.isOptionClicked = false;
    if (this.$route.name !== 'Search') {
      this.searchTerm = "";
    } else {
      this.searchTerm = localStorage.searchTerm;
    }
    // The `admin` flag was previously set here as a data
    // field assignment in mounted(). Replaced with a
    // computed property below so it stays in sync as the
    // user navigates between admin and customer routes
    // without a full page reload — Head.vue persists across
    // route changes (it lives outside <router-view>) so
    // mounted() only fires once and the old data-field
    // approach left the header hidden after navigating
    // away from /admin via SPA routing.
    this.settingsDetails();
    if (this.currentUser) {
      this.getCompanies();
      this.loadCartCount();
      // Hydrate the autocomplete's company-machine filter as
      // soon as we have a company selection. selectedCompanyId
      // is initialized from localStorage.companyId in data(),
      // so on subsequent visits this fires immediately. On a
      // first-ever visit (no localStorage company) the filter
      // stays empty and the autocomplete passes through
      // unfiltered until selectCompany runs.
      if (this.selectedCompanyId) {
        this.loadUserMachineNames();
      }
    }
    // Initial Test Mode state read. canSeeTestMode gates
    // the actual API call, so non-Masters and non-test
    // companies just no-op here at zero cost.
    this.refreshTestModeState();
    // Apply the persisted font size on mount so the saved choice
    // (e.g. LARGE) actually takes effect when the page loads. Without
    // this, fontSize is read into data() but the html element has no
    // matching font-size-* class, so the page renders at default
    // until the user clicks the toggle again.
    this.applyFontSize(this.fontSize);

    // Mirror the color-mode class main.js preloaded onto <html> so the toggle
    // shows the correct icon (sun / sun-half-moon / moon) on every page load.
    var _h = document.documentElement;
    // A legacy mid-mode class reads as 'dark' for the icon — mid is no longer a selectable state,
    // and main.js migrates the stored preference to dark on load.
    this.colorMode = (_h.classList.contains('dark-mode') || _h.classList.contains('mid-mode'))
      ? 'dark' : 'light';
    Vue.prototype.$cartBus = Vue.prototype.$cartBus || new Vue();
    Vue.prototype.$cartBus.$on('cart-updated', this.handleCartUpdated);
    // Keep the color-mode toggle icon in sync when the debug overlay's "Target
    // CSS file" dropdown switches the site theme (App.vue emits this).
    Vue.prototype.$cartBus.$on('color-mode-changed', this.onColorModeChanged);
    // Keep the bug button's debug ON/OFF state in sync with the Account Setup
    // checkbox (both toggle the same overlay via this event).
    Vue.prototype.$cartBus.$on('debug-overlay-toggle', this.onDebugOverlaySync);
    // Customer Insights → switch the active company when a Sales rep selects a
    // customer (browse "as" that customer) — silent, no reload.
    this.$root.$on('report-company-switch', this.onReportCompanySwitch);
    // Sales Insights → keep the header's compact From/To pickers in sync with the report's window.
    this.$root.$on('report-date-current', this.onReportDateCurrent);
    // Sales Insights → remember the report's currently selected customer so the
    // parts-request contact modal can prefill the customer company for reps.
    this.$root.$on('report-customer-context', this.onReportCustomerContext);
    // After a rep places an order/quote (Cart Success / SavedRFQ), send @jcsteele.com / non-Customer staff
    // back to their NC999 home company (they may have switched into a customer to quote for them).
    this.$root.$on('jcs-return-home', this.returnHomeToNC999);
    // Subscribe to the cross-component header-search clear
    // event. Page files (Search/Order/RFQ) emit 'cleared' from
    // their clearHeaderSearch method (× button on the SEARCHING
    // FOR banner). When fired, the handler below resets this
    // header's search input + related dropdown state so the
    // dismissed term doesn't keep appearing in the input field.
    // Lazy-init pattern mirrors $cartBus above.
    Vue.prototype.$headerSearchBus =
      Vue.prototype.$headerSearchBus || new Vue();
    Vue.prototype.$headerSearchBus.$on('cleared',
      this.handleHeaderSearchCleared);
    Vue.filter("companyPhone", function (companyPhone) {
      return companyPhone
        .replace(/[^0-9]/g, "")
        .replace(/(\d{3})(\d{3})(\d{4})/, "($1) $2-$3");
    });
  },
beforeDestroy() {
  if (Vue.prototype.$cartBus) {
    Vue.prototype.$cartBus.$off('cart-updated', this.handleCartUpdated);
    Vue.prototype.$cartBus.$off('color-mode-changed', this.onColorModeChanged);
    Vue.prototype.$cartBus.$off('debug-overlay-toggle', this.onDebugOverlaySync);
    this.$root.$off('report-company-switch', this.onReportCompanySwitch);
    this.$root.$off('report-date-current', this.onReportDateCurrent);
    this.$root.$off('report-customer-context', this.onReportCustomerContext);
    this.$root.$off('jcs-return-home', this.returnHomeToNC999);
  }

  // Mirror the $cartBus teardown pattern. Guarded against
  // missing bus instance (e.g., if mounted never completed
  // before destroy due to fast nav).
  if (Vue.prototype.$headerSearchBus) {
    Vue.prototype.$headerSearchBus.$off('cleared',
      this.handleHeaderSearchCleared);
  }

  if (this.cartWarningTimer) {
    clearTimeout(this.cartWarningTimer);
    this.cartWarningTimer = null;
  }

  if (this.expiryCountdownInterval) {
    clearInterval(this.expiryCountdownInterval);
    this.expiryCountdownInterval = null;
  }
},
  data() {
    return {
      searchOption: [],
      disabled: false,
      error: false,
      // Role names (from the DB) offered in the admin-only "view as role"
      // dropdown. Loaded once for real admins via /user/GetRoleSelectItems.
      roleOptions: [],
      companyUsers: [],   // users of the selected company, for the admin-only "View as user" dropdown
      // Sales Insights header date pickers (compact From/To, left of the shortcut buttons). Synced FROM the
      // report via the 'report-date-current' event; editing one pushes the new window back via 'report-date-range'.
      rptFrom: "",
      rptTo: "",
      // Mirrors localStorage.jcs_debugOverlay so the bug button (for staff) can
      // show ON/OFF state. Kept in sync with the Account Setup checkbox via the
      // $cartBus "debug-overlay-toggle" event (see mounted/beforeDestroy).
      debugOverlayOn:
        typeof localStorage !== "undefined" &&
        localStorage.getItem("jcs_debugOverlay") === "1",
      // Test Mode toggle state — read on mount via the
      // GET /Company/{id} endpoint and updated when the
      // user clicks the toggle. Independent of the company
      // dropdown's selectedCompanyId — the user can switch
      // companies and the gate hides; switch back and we
      // re-fetch the current state.
      testModeActive: false,
      testModeBusy: false,
      companyLogo: "",
      companyLogoFileName: "",
      companyPhone: "",
      // ===== Contact modal (mobile header contact icon) =====
      // Mirrors the Search page's "Email the Parts Team" form, plus a
      // tap-to-call phone block at the top. Posts to the same endpoint.
      showContactModal: false,
      contactSending: false,
      contactSuccess: false,
      contactError: "",
      contactFile: null,
      contactFileError: "",
      contactForm: {
        customerName: "",
        customerEmail: "",
        customerCompany: "",
        // Sales-rep "request parts for my customer" flow only: the END customer's
        // on-site contact (e.g. Paul Runyon). customerName/customerEmail stay the
        // REP (reply-to); customerCompany becomes the customer's company.
        contactName: "",
        contactEmail: "",
        // Quantity needed — prefilled with the average past-order quantity for the filtered part.
        quantity: "",
        machineDescription: "",
        description: "",
      },
      // Customer broadcast from the Sales Insights report (report-customer-context):
      // the exactly-one selected customer + its filtered machine/part text, used to prefill the parts-request form.
      reportCustomer: { customerId: "", customerName: "", machineText: "", partText: "", partCode: "", summary: "", avgQty: "" },
      // Sales-rep contact picker: known portal users at the selected customer (convenience dropdown; the
      // contact fields stay free-text for contacts without a portal login). contactUserSel = chosen user id.
      contactUsers: [],
      contactUsersLoading: false,
      contactUserSel: "",
      // Header rep row (on a customer company): the customer's contacts + the one the rep picked (prefills the
      // Request Parts contact). Loaded via the rep-allowed /user/company-contacts endpoint.
      repContacts: [],
      repContactSel: "",
      searchTerm: "" || localStorage.searchTerm,
      code: "" || localStorage.code,
      description: "" || localStorage.description,
      loader: false,
      msg: {
        term: "",
        isTrue: false,
      },
      url: "../assets/",
      showCustomDropdown: false,
      isOptionClicked: false,
      selectedMachineName: '',
      selectedAssembly: '',
      // Master Assembly id captured when the user picks a
      // suggestion in selectOption. Lets the typed-term/Enter
      // path (search()) forward an exact assembly id when the
      // user picked a suggestion and then pressed Enter, so the
      // Search page can disambiguate same-named assemblies.
      // 0 = "no id known" (free-typed term) → Search page falls
      // back to name resolution. Reset whenever the input is
      // cleared so a stale id can't leak into a later free-typed
      // search.
      selectedAssemblyId: 0,
      companies: [],
      selectedCompanyId: localStorage.companyId || '',
      // ===== AUTOCOMPLETE COMPANY FILTER =====
      // Array of normalized machine NAMES for the current
      // company. Populated by loadUserMachineNames() on mount
      // and on company change. Used in getSearchedParts to
      // filter out part suggestions whose machineName doesn't
      // match any of this company's machines — prevents users
      // from picking a suggestion that won't hydrate (because
      // the part's machine doesn't belong to their company).
      //
      // NAMES (not IDs) because of a documented gotcha on this
      // project: the machineId returned by getPartSuggestion is
      // the master Machine ID across all customers, while
      // getMachinesByCompany returns CompanyMachine IDs scoped
      // to one customer's instances of those machines. The
      // two ID spaces don't overlap. Name-based matching is the
      // established workaround used by the page-level matcher
      // (see applyPendingHeaderMachine on Search/Order/RFQ).
      userMachineNames: [],
      showCompanyDropdown: false,
      companySearchTerm: '',
      showClosedCompanies: false,
      cartItemCount: 0,
      // Parts count for the active quote (cartIdRFQ), shown on
      // the Quotes link inside the consolidated View Cart entry.
      // Independent of cartItemCount so each link can reflect
      // exactly its own content rather than a combined total
      // that doesn't match any single page.
      quoteItemCount: 0,
      cartExpiryTimer: null,
      cartWarningTimer: null,
      showExpiryWarning: false,
      showSavingOverlay: false,
      expiryCountdownSeconds: 300,
      expiryCountdownInterval: null,
      // Google Translate widget collapse state. Stored in localStorage
      // (same pattern as companyId / searchTerm / etc. above) rather
      // than cookies so it persists across sessions without a server
      // Translate widget — ALWAYS starts collapsed on page
      // load. We deliberately don't read from localStorage here
      // because the user wants the floating utility widgets
      // tucked away at the start of every page; expanding is
      // an active gesture, not a sticky preference.
      translateCollapsed: true,
      // Font size cycle (NORMAL → MEDIUM → LARGE → NORMAL) and
      // collapse state for the floating font-size toggle.
      // The applied font size IS persisted (so a user who
      // chose LARGE keeps seeing LARGE), but the toggle's
      // collapsed-or-expanded state is not — same reasoning
      // as translateCollapsed above.
      fontSize: localStorage.fontSize || 'NORMAL',
      fontToggleCollapsed: true,
      // Dark mode. Synced from the `dark-mode` class on <html> in
      // mounted() (main.js preloads the class from localStorage before first
      // paint), so this just mirrors it for the icon swap.
      colorMode: 'light'
    };
  },
  watch: {
    // Load the DB role list for the "view as role" dropdown as soon as we know
    // the signed-in user is really an admin (immediate => also runs on mount).
    isRealAdmin: {
      immediate: true,
      handler(value) {
        if (value) { this.loadRoleOptions(); this.loadCompanyUsers(); }
      }
    },
    // Reload the company's users (for "View as user") whenever the selected company changes.
    selectedCompanyId() {
      if (this.isRealAdmin) this.loadCompanyUsers();
      // Rep row: refresh the active customer's contacts for the User dropdown on any company switch.
      this.loadRepContacts();
    },
    // Refetch the company list whenever an admin preview starts/stops/retargets, so the header
    // selector always mirrors the previewed identity (getCompanies sends viewAsUserId/previewRole).
    // getCompanies' own still-valid logic re-pins to NC999 when the current pick leaves the list.
    viewingAsUser(nv, ov) {
      if (String((nv && nv.id) || '') !== String((ov && ov.id) || '')) this.getCompanies();
    },
    viewingAsRole(nv, ov) {
      // A user-preview change also flips the role — skip here so we don't fetch twice; the
      // viewingAsUser watcher owns that case.
      if (this.viewingAsUser) return;
      if (nv !== ov) this.getCompanies();
    },
    searchTerm(value) {
      this.validateSearchField(value);
      this.formDisabled(value);
      if (this.searchTerm.length > 2) {
        this.getSearchedParts(value);
      } else {
        delete localStorage.description;
        this.description = "";
        this.searchOption = [];
      }
    },
    $route(to, from) {
      if (from.name == "Index") {
        this.isOptionClicked = false;
      }
      // Forget the header search after leaving the Search page, so the next
      // visit starts blank rather than showing the last term (user request).
      if (from && from.name === "Search" && (!to || to.name !== "Search")) {
        this.searchTerm = "";
        this.description = "";
        delete localStorage.searchTerm;
        delete localStorage.description;
        this.showCustomDropdown = false;
      }
    }
  },
  methods: {
    // ===== CONTACT MODAL (mobile header contact icon) =====
    // Opens the contact modal, pre-filling name/email/company from the logged-
    // in user + selected company so the form is mostly ready to send.
    openContactModal() {
      var u = this.currentUser || {};
      if (!this.contactForm.customerName) {
        this.contactForm.customerName =
          u.firstName || u.FirstName || u.first_name ||
          (u.email && u.email.indexOf("@") > 0 ? u.email.substring(0, u.email.indexOf("@")) : "");
      }
      if (!this.contactForm.customerEmail) {
        this.contactForm.customerEmail = u.email || u.Email || "";
      }
      if (this.isSalesRequester) {
        // Sales-rep flow: prefill the CUSTOMER company from the report's currently
        // selected customer (re-synced each open so switching customers in the
        // report updates it); fall back to the active company selection. Name /
        // email stay the rep (set above) so the reply routes back to the rep.
        var rc = (this.reportCustomer && this.reportCustomer.customerName)
          ? this.reportCustomer.customerName : "";
        if (rc) {
          this.contactForm.customerCompany = rc;
        } else if (!this.contactForm.customerCompany) {
          var scn = this.selectedCompanyName;
          if (scn && scn !== "Select Company") this.contactForm.customerCompany = scn;
        }
        // Seed the request with a summative description of the rep's filter narrowing (customer / territory /
        // rep / part / machine / window) — what the parts team needs to act. Only when empty (never clobber).
        var summary = (this.reportCustomer && this.reportCustomer.summary) ? this.reportCustomer.summary : "";
        if (summary && !this.contactForm.description) this.contactForm.description = summary;
        // Machine / Equipment: prefer the machine-code filter text; otherwise resolve it from the primary
        // filtered part code the same way the header product search does (part → machine + assembly).
        var mt = (this.reportCustomer && this.reportCustomer.machineText) ? this.reportCustomer.machineText : "";
        if (mt && !this.contactForm.machineDescription) {
          this.contactForm.machineDescription = mt;
        } else if (!mt && this.reportCustomer && this.reportCustomer.partCode) {
          this.resolvePartMachine(this.reportCustomer.partCode);
        }
        // Prefill Quantity with the average past-order quantity for the filtered part (rep can adjust).
        var aq = (this.reportCustomer && this.reportCustomer.avgQty) ? this.reportCustomer.avgQty : "";
        if (aq && !this.contactForm.quantity) this.contactForm.quantity = aq;
        // New-tab (Search page) fallback — if the report context isn't present (a fresh "Quote this part" tab),
        // prefill Machine / Quantity / request from the URL params that link carried, so the form is ready.
        var _uq = (this.$route && this.$route.query) || {};
        if (!this.contactForm.machineDescription && _uq.machineName) this.contactForm.machineDescription = String(_uq.machineName);
        if (!this.contactForm.quantity && _uq.avgQty) this.contactForm.quantity = String(_uq.avgQty);
        if (!this.contactForm.description && (_uq.code || _uq.reason)) {
          var _partLine = "Provide a quote for the following part: " + String(_uq.code || "") +
            (_uq.partDesc ? " — " + String(_uq.partDesc) : "") +
            (_uq.machineName ? " for " + String(_uq.machineName) : "") + ".";
          var _adviseLine = "Please advise on availability, pricing, and lead time.";
          if (_uq.reason) {
            // Lead with the AI's recap of what the customer asked for (the Email Insights tooltip), then the
            // match-probability caveat, then the quote request, then the availability ask — each on its own line.
            var _custRef = _uq.switchCompanyCode ? ("Customer " + String(_uq.switchCompanyCode)) : "The customer";
            var _blocks = [_custRef + " reached out about a part. " + String(_uq.reason).trim()];
            if (_uq.prob) _blocks.push("Double-check the below suggested part(s) — the AI's estimated match certainty is around " + String(_uq.prob) + "%.");
            _blocks.push(_partLine);
            _blocks.push(_adviseLine);
            this.contactForm.description = _blocks.join("\n\n");
          } else {
            this.contactForm.description = _partLine + "\n\n" + _adviseLine;
          }
        }
        // Load the customer's known portal contacts for the picker (by customer code). On a fresh "Quote this
        // part" tab the report context is absent, so fall back to the switched-in company code from the URL /
        // active selection so the contact dropdown still populates.
        var _contactCode = (this.reportCustomer && this.reportCustomer.customerId) ? this.reportCustomer.customerId
          : (_uq.switchCompanyCode ? String(_uq.switchCompanyCode) : (localStorage.getItem("companyCode") || ""));
        this.loadContactUsers(_contactCode);
      } else if (!this.contactForm.customerCompany) {
        var cn = this.selectedCompanyName;
        if (cn && cn !== "Select Company") this.contactForm.customerCompany = cn;
      }
      this.showContactModal = true;
    },
    // Fetch the selected customer's known portal users for the contact picker. Convenience only —
    // the name/email fields stay editable for contacts who aren't portal users. Keyed by customer code.
    loadContactUsers(code) {
      this.contactUsers = [];
      this.contactUserSel = "";
      if (!code) return;
      this.contactUsersLoading = true;
      var self = this;
      feapiService.getCompanyContacts(code)
        .then(function (d) {
          var list = (d && d.users) ? d.users : [];
          self.contactUsers = list.map(function (u) {
            var full = ((u.firstName || "") + " " + (u.lastName || "")).trim();
            return {
              id: u.id,
              name: full,
              email: u.email || "",
              label: (full || u.email || "Contact") + (u.email ? " — " + u.email : "")
            };
          });
        })
        .catch(function () { self.contactUsers = []; })
        .finally(function () { self.contactUsersLoading = false; });
    },
    // Picking a known contact fills the (still-editable) name + email fields.
    onContactUserPick() {
      var sel = this.contactUserSel;
      if (!sel) return;
      var u = this.contactUsers.find(function (x) { return String(x.id) === String(sel); });
      if (!u) return;
      if (u.name) this.contactForm.contactName = u.name;
      this.contactForm.contactEmail = u.email || "";
    },
    // Header rep row: load the ACTIVE customer's contacts (rep-allowed, by company code) for the User dropdown.
    // No-op unless a non-Customer rep is on a non-NC999 company. Reads localStorage.companyCode (set on switch).
    loadRepContacts() {
      this.repContacts = [];
      this.repContactSel = "";
      if (!this.currentUser || this.effectiveRoleName === "Customer") return;
      var code = (localStorage.companyCode || "").trim();
      if (!code || /NC999/i.test(code)) return;
      var self = this;
      feapiService.getCompanyContacts(code)
        .then(function (d) {
          var list = (d && d.users) ? d.users : [];
          self.repContacts = list.map(function (u) {
            var full = ((u.firstName || "") + " " + (u.lastName || "")).trim();
            return { id: u.id, name: full, email: u.email || "", label: (full || u.email || "Contact") + (u.email ? " — " + u.email : "") };
          });
        })
        .catch(function () { self.repContacts = []; });
    },
    // Rep picked the customer user "wanting the purchase" → prefill the Request Parts contact (name + email).
    onRepContactPick() {
      var u = this.repContacts.find(function (x) { return String(x.id) === String(this.repContactSel); }.bind(this));
      if (!u) return;
      if (u.name) this.contactForm.contactName = u.name;
      this.contactForm.contactEmail = u.email || "";
    },
    // Resolve a part code → its Machine + Assembly the same way the header product search does
    // (getPartSuggestion returns machineName + assembly per code), then fill Machine / Equipment and fold the
    // machine/assembly into the request summary. Used when the report was narrowed by PART (no machine-code filter).
    resolvePartMachine(code) {
      var self = this;
      if (!code) return;
      feapiService.getPartSuggestion({ searchTerm: code, showWearPart: false })
        .then(function (res) {
          var list = res || [];
          if (!list.length) return;
          var norm = function (s) { return String(s == null ? "" : s).toLowerCase().replace(/[^a-z0-9]/g, ""); };
          var want = norm(code);
          var hit = list.find(function (x) { return norm(x.code) === want; }) || list[0];
          var mn = (hit.machineName || "").trim();
          var asm = (hit.assembly || "").trim();
          if (!mn && !asm) return;
          if (!self.contactForm.machineDescription) {
            self.contactForm.machineDescription = mn + (asm ? " / " + asm : "");
          }
          // Fold the machine/assembly into the request summary (once), up front.
          var line = "Machine: " + (mn || "(unspecified)") + (asm ? ", Assembly: " + asm : "") + ".";
          var desc = self.contactForm.description || "";
          if (desc.indexOf(line) === -1) {
            self.contactForm.description = desc ? (line + " " + desc) : line;
          }
        })
        .catch(function () { /* non-fatal — the rep can type the machine */ });
    },
    onContactFileChange(e) {
      var files = e.target.files || (e.dataTransfer && e.dataTransfer.files);
      if (!files || !files.length) {
        this.contactFile = null;
        return;
      }
      var ext = files[0].name.split(".").pop().toLowerCase();
      if (["jpg", "jpeg", "png"].indexOf(ext) === -1) {
        if (this.$refs.contactFile) this.$refs.contactFile.value = null;
        this.contactFile = null;
        this.contactFileError = "Only JPEG, JPG and PNG files are supported.";
        return;
      }
      if (files[0].size > 1024 * 1024) {
        if (this.$refs.contactFile) this.$refs.contactFile.value = null;
        this.contactFile = null;
        this.contactFileError = "Maximum file size of 1 MB.";
        return;
      }
      this.contactFile = files[0];
      this.contactFileError = "";
    },
    submitPartsInquiry(bvModalEvent) {
      // Keep the modal open while sending; the OK label flips to "Sending…"
      // and a success/error banner replaces the form on resolve.
      if (bvModalEvent) bvModalEvent.preventDefault();
      if (!this.contactFormValid || this.contactSending) return;

      this.contactSending = true;
      this.contactError = "";

      var fd = new FormData();
      fd.append("CustomerName", this.contactForm.customerName.trim());
      fd.append("CustomerEmail", this.contactForm.customerEmail.trim());
      if (this.contactForm.customerCompany) {
        fd.append("CustomerCompany", this.contactForm.customerCompany.trim());
      }
      // Sales-rep request only: the customer's on-site contact. ContactName's
      // presence flips the email to the rep-request layout server-side.
      if (this.isSalesRequester && this.contactForm.contactName && this.contactForm.contactName.trim()) {
        fd.append("ContactName", this.contactForm.contactName.trim());
        if (this.contactForm.contactEmail && this.contactForm.contactEmail.trim()) {
          fd.append("ContactEmail", this.contactForm.contactEmail.trim());
        }
      }
      if (this.isSalesRequester && this.contactForm.quantity && String(this.contactForm.quantity).trim()) {
        fd.append("Quantity", String(this.contactForm.quantity).trim());
      }
      if (this.contactForm.machineDescription) {
        fd.append("MachineDescription", this.contactForm.machineDescription.trim());
      }
      // Linkable suggested part (Sales-rep flow): pass the Search URL that HYDRATES the machine/assembly/part so
      // the parts team can click straight to it, AND carry the customer company (switchCompany*) so the link opens
      // with the correct company selected — the parts team sees the part in that customer's context (pricing /
      // availability). The company comes from the quote tab's URL; falls back to the currently active company
      // (which on that tab IS the customer). Quote intent is left off — this is a lookup link, not a new quote.
      if (this.isSalesRequester) {
        var _q = (this.$route && this.$route.query) || {};
        var _partNo = _q.code || (this.reportCustomer && this.reportCustomer.partCode) || "";
        if (_partNo) {
          fd.append("PartNumber", String(_partNo));
          if (_q.partDesc) fd.append("PartDescription", String(_q.partDesc));
          var _linkQ = { code: String(_partNo), allParts: "true" };
          if (_q.machineName) _linkQ.machineName = String(_q.machineName);
          if (_q.assembly) _linkQ.assembly = String(_q.assembly);
          if (_q.assemblyId) _linkQ.assemblyId = String(_q.assemblyId);
          var _swId = _q.switchCompanyId || this.selectedCompanyId || "";
          var _swCode = _q.switchCompanyCode || localStorage.getItem("companyCode") || "";
          var _swName = _q.switchCompanyName || this.selectedCompanyName || "";
          if (_swId) _linkQ.switchCompanyId = String(_swId);
          if (_swCode) _linkQ.switchCompanyCode = String(_swCode);
          if (_swName && _swName !== "Select Company") _linkQ.switchCompanyName = String(_swName);
          fd.append("PartUrl", window.location.origin + this.$router.resolve({ name: "Search", query: _linkQ }).href);
        }
      }
      fd.append("Description", this.contactForm.description.trim());
      if (this.contactFile) {
        fd.append("InquiryFile", this.contactFile);
      }

      var self = this;
      feapiService.submitPartsInquiry(fd)
        .then(function () {
          self.contactSending = false;
          self.contactSuccess = true;
          setTimeout(function () {
            self.showContactModal = false;
          }, 1800);
        })
        .catch(function (err) {
          self.contactSending = false;
          var msg = "Please try again or call (800) 278-3353.";
          var traceId =
            err && err.error && err.error.response && err.error.response.data
              ? err.error.response.data.traceId
              : null;
          if (traceId) {
            msg = "Please try again or call (800) 278-3353. (TraceID: " + traceId + ")";
          }
          self.contactError = msg;
        });
    },
    resetContactForm() {
      // Fired on any close. Clear everything except name/email so a returning
      // user doesn't have to retype them.
      this.contactSending = false;
      this.contactSuccess = false;
      this.contactError = "";
      this.contactFile = null;
      this.contactFileError = "";
      this.contactForm.machineDescription = "";
      this.contactForm.description = "";
      // Clear the per-customer contact fields so the next request doesn't carry a
      // stale contact; company re-syncs from the report on the next open.
      this.contactForm.contactName = "";
      this.contactForm.contactEmail = "";
      this.contactForm.quantity = "";
      this.contactUsers = [];
      this.contactUserSel = "";
      if (this.$refs.contactFile) this.$refs.contactFile.value = null;
    },
    // ===== TEST MODE TOGGLE =====
    // Read the current IsTestModeActive value from the
    // backend so the button reflects reality. Called on
    // mount and after the company selector changes —
    // localStorage doesn't track this because Test Mode
    // is a property of the company (server-side), not of
    // the browser session.
    refreshTestModeState() {
      if (!this.canSeeTestMode) {
        this.testModeActive = false;
        return;
      }
      var cid = this.selectedCompanyId;
      var self = this;
      axios.get("/Company/" + cid).then(function (res) {
        var data = (res && res.data) || {};
        self.testModeActive = !!data.isTestModeActive;
      }).catch(function (err) {
        console.warn("[TestMode] read failed:", err);
        self.testModeActive = false;
      });
    },

    // Flip the flag. Optimistically updates the button so
    // the click feels instant; reverts if the server
    // rejects (Master check fails, or company not in
    // allowlist). Disabled state during the request prevents
    // double-clicks producing inconsistent state.
    toggleTestMode() {
      if (this.testModeBusy) return;
      var cid = this.selectedCompanyId;
      if (!cid) return;
      var newState = !this.testModeActive;
      this.testModeBusy = true;
      this.testModeActive = newState; // optimistic
      var self = this;
      axios.post("/Company/" + cid + "/test-mode", { enabled: newState })
        .then(function (res) {
          var data = (res && res.data) || {};
          self.testModeActive = !!data.isTestModeActive;
        })
        .catch(function (err) {
          console.error("[TestMode] toggle failed:", err);
          self.testModeActive = !newState; // revert
        })
        .finally(function () {
          self.testModeBusy = false;
        });
    },

    // Toggle the Google Translate widget between collapsed (small globe
    // handle on the right edge) and expanded (full pill with language
    // dropdown). State is mirrored to localStorage so the user's last
    // choice is restored on the next page load — matches the same
    // pattern used for selectedCompanyId, searchTerm, etc. above.
    toggleTranslateCollapsed() {
      this.translateCollapsed = !this.translateCollapsed;
      // Don't persist — every page load starts collapsed by
      // design (see data() comments). Storing here would
      // contradict that.
    },

    // Open the bug report page. Triggered from the bug-report
    // toggle in the bottom-right utility stack. Uses the router
    // when the route exists, falls back to a hard navigation so
    // the link works even on installations where /report-bug
    // is a static page outside the SPA.
    goToBugReport() {
      try {
        if (this.$router) {
          this.$router.push('/report-bug');
          return;
        }
      } catch (e) {
        // Router push throws if the route doesn't exist —
        // fall through to hard nav below.
      }
      window.location.href = '/report-bug';
    },

    // Bug button (bottom-right util rail): staff (admin/Master) toggle the
    // layout DEBUG OVERLAY in place; everyone else goes to /report-bug. Split
    // requested so admins get the dev tool while customers still get the form.
    onBugButtonClick() {
      if (this.canToggleDebug) {
        this.toggleDebugOverlay();
      } else {
        this.goToBugReport();
      }
    },

    // Flip the global layout debug overlay. App.vue's onDebugOverlayToggle
    // (subscribed to this same $cartBus event) renders it + persists
    // localStorage.jcs_debugOverlay, so here we only emit the new state.
    toggleDebugOverlay() {
      var next = !this.debugOverlayOn;
      this.debugOverlayOn = next; // instant button feedback
      try {
        if (Vue.prototype.$cartBus) {
          Vue.prototype.$cartBus.$emit('debug-overlay-toggle', next);
        }
      } catch (e) {
        /* ignore */
      }
    },

    // Sync handler: when the overlay is toggled anywhere (e.g. the Account
    // Setup checkbox), reflect the state on the bug button.
    onDebugOverlaySync(on) {
      this.debugOverlayOn = !!on;
    },

    // Cycle the font size: NORMAL → MEDIUM → LARGE → NORMAL. Each step
    // updates the html element's class so the global rules in
    // steele-consistency-fix.css (section 9) take effect, and persists
    // the choice to localStorage.fontSize so it survives reloads.
    cycleFontSize() {
      const order = ['NORMAL', 'MEDIUM', 'LARGE'];
      const idx = order.indexOf(this.fontSize);
      const next = order[(idx + 1) % order.length];
      this.fontSize = next;
      localStorage.fontSize = next;
      this.applyFontSize(next);
    },

    // Apply a font size as an html class. Removes any existing
    // font-size-* classes first so the cycle is clean.
    applyFontSize(size) {
      const html = document.documentElement;
      html.classList.remove(
        'font-size-normal',
        'font-size-medium',
        'font-size-large'
      );
      html.classList.add('font-size-' + size.toLowerCase());
    },

    // Toggle the font-size selector between collapsed (small Aa pill)
    // Toggle the font-size selector between collapsed (small Aa pill)
    // and expanded (Aa + size label + close button). Not persisted
    // — every page load starts collapsed by design.
    toggleFontToggleCollapsed() {
      this.fontToggleCollapsed = !this.fontToggleCollapsed;
    },

    // Cycle the color mode: light → mid-dark → dark → light. Sets the matching
    // class on <html> (themed by darkmode.css / mid-mode.css) and persists the
    // choice (colorMode) so it sticks across pages and sessions. Available to
    // every user type via the floating util-toggles stack.
    // Mirror an externally-driven color-mode change (the debug overlay's Target
    // CSS file dropdown) into the toggle icon's state.
    onColorModeChanged(mode) {
      // 'mid' can still arrive from the admin debug overlay's CSS-file preview; show it as dark
      // so the moon icon matches the dark page, since the toggle itself can no longer select mid.
      if (mode === 'mid') { this.colorMode = 'dark'; return; }
      if (mode === 'dark' || mode === 'light') this.colorMode = mode;
    },
    // TOGGLE light ⇄ dark. Mid-dark was retired from the toggle 2026-07-26 (mid-mode.css stays on
    // disk + imported, just never selected here). Always strips BOTH classes first, so a session
    // that landed in mid via the debug overlay is cleaned up by the first click.
    cycleColorMode() {
      const next = this.colorMode === 'light' ? 'dark' : 'light';
      this.colorMode = next;
      const html = document.documentElement;
      html.classList.remove('dark-mode', 'mid-mode');
      if (next === 'dark') html.classList.add('dark-mode');
      try {
        localStorage.colorMode = next;
        localStorage.darkMode = next === 'dark' ? '1' : ''; // back-compat
      } catch (e) {
        // localStorage unavailable (private mode) — non-fatal.
      }
    },

    search() {
      this.loader = true;
      if (this.searchTerm !== "") {
        // Activity trail: the COMMITTED header search (Enter / magnifier).
        recordSearch("header", this.code || this.searchTerm);
        var queryParams = {
          code: this.code ? this.code : this.searchTerm,
          description: this.description && this.description !== "undefined" ? this.description : '',
          // Same intent as selectOption — the user typed a
          // specific code/term, so the Search page should
          // honor it without pre-filtering to wear-only.
          allParts: 'true'
        };
        if (this.selectedMachineName) {
          queryParams.machineName = this.selectedMachineName;
        }
        if (this.selectedAssembly) {
          queryParams.assembly = this.selectedAssembly;
          // Forward the disambiguating assembly id ONLY when we
          // actually have one (i.e. the user picked a suggestion
          // then pressed Enter). A free-typed term carries no id
          // — leaving assemblyId off lets the Search page resolve
          // by name as it always has, so this is purely additive.
          if (this.selectedAssemblyId) {
            queryParams.assemblyId = this.selectedAssemblyId;
          }
        }
        this.$router.push({
          query: Object.assign({}, this.$route.query, queryParams),
          name: "Search",
        });
        localStorage.searchTerm = this.searchTerm;
        localStorage.code = this.code;
        localStorage.description = this.description;
      } else {
        this.msg.isTrue = true;
        this.msg.term = "";
      }
    },
    selectOption(value) {
      this.isOptionClicked = true;
      // Activity trail: a suggestion CLICK. Separate funnel from search() above —
      // this routes to Search itself and never calls it.
      recordSearch("header-suggest", (value && (value.code || value.value)) || "");
      this.searchTerm = value.value;
      this.code = value.code;
      this.description = '';
      this.selectedMachineName = value.machineName || '';
      this.selectedAssembly = value.assembly || '';
      this.selectedAssemblyId = value.assemblyId || 0;
      this.showCustomDropdown = false;
      delete localStorage.description;
      this.$router.push({
        query: {
          code: value.code,
          machineName: value.machineName || '',
          assembly: value.assembly || '',
          // Disambiguate the assembly by ID. Many assemblies
          // share the same NAME across the catalog (e.g. there
          // are 36 named "BACK-UP SPIRALS"), and some of those
          // duplicates live under the SAME machine — so the
          // Search page resolving purely by `assembly` name can
          // land on the wrong sub-assembly and fail to surface
          // the part the user actually clicked. The suggestion
          // object already carries assemblyId (the master
          // Assembly id), so pass it along; the Search page
          // prefers an exact id match over the name cascade.
          // 0/absent is the documented "no id — fall back to
          // name" sentinel, preserving older-link behavior.
          assemblyId: value.assemblyId || 0,
          // Explicit "show all parts" intent: the user picked
          // a SPECIFIC part code from autocomplete, so the
          // Search page should not pre-filter to wear-only and
          // hide their pick. The Search page reads this flag
          // and unchecks Show Wear Parts Only on mount when
          // present.
          allParts: 'true'
        },
        name: "Search",
      });
      localStorage.searchTerm = value.value;
      localStorage.code = value.code;
    },
    onInputBlur() {
      setTimeout(() => {
        this.showCustomDropdown = false;
      }, 200);
    },
    settingsDetails() {
      apiService
        .getCompanySettings()
        .then((request) => {
          this.settingsSuccessful(request);
        })
        .catch((err) => this.settingsFailed(err));
    },
    settingsSuccessful(request) {
      (this.companyName = request.companyName),
        (this.companyAddress1 = request.companyAddress1),
        (this.companyAddress2 = request.companyAddress2),
        (this.companyCity = request.companyCity),
        (this.companyState = request.companyState),
        (this.companyZip = request.companyZip),
        (this.companyCountry = request.companyCountry),
        (this.companyPhone = request.companyPhone),
        (this.companyEmail = request.companyEmail),
        (this.companyFax = request.companyFax),
        (this.companyLogo = request.companyLogo),
        (this.companyLogoFileName = request.companyLogoFileName);
    },
    settingsFailed(err) {
      this.$bvToast.toast("TraceID: " + err.error.response.data.traceId, {
        title: err.error.response.data.title,
        autoHideDelay: 10000,
        appendToast: false,
        variant: "danger",
        solid: true,
      });
    },
    validateSearchField(value) {
      if (value.length < 3 && value.length > 0) {
        this.msg.isTrue = true;
      } else {
        this.msg.isTrue = false;
      }
    },
    formDisabled(value) {
      if (value.length < 3 && value.length > 0) {
        this.disabled = true;
      } else {
        this.disabled = false;
      }
    },
    getSearchedParts(value) {
      this.code = "";
      // A fresh keystroke invalidates any previously-picked
      // suggestion, so drop its assembly id too — otherwise a
      // stale id from an earlier pick could ride along with a
      // newly free-typed term and mis-disambiguate on the
      // Search page.
      this.selectedAssemblyId = 0;
      if (!value) return;
      var self = this;
      feapiService.getPartSuggestion({
        searchTerm: value,
        // Search ALL parts, not just wear parts. Hardcoding
        // showWearPart: true scoped the autocomplete to the
        // wear-parts subset, which made it impossible to
        // type-ahead any non-wear part code (e.g. CFC D 0091
        // CB) — those parts exist in the master Parts table
        // but typically aren't in the company-specific Wears
        // table, so they never surfaced as suggestions. The
        // wear-only filter is still applied separately on
        // the search results page via the
        // "Show Wear Parts Only" checkbox.
        showWearPart: false
      })
        .then(function (res) {
          // ===== COMPANY-MACHINE FILTER (NAME-BASED) =====
          // Drop any suggestion whose machineName doesn't match
          // any of the current company's machine labels. This
          // prevents users from picking a suggestion that won't
          // successfully hydrate on the destination page
          // (because that page's applyPendingHeaderMachine
          // matcher would fail to find the machine in the
          // company's loaded list).
          //
          // Matching is intentionally fuzzy (alphanumeric-only,
          // substring in either direction) because the same
          // machine can be labeled differently between the two
          // sources — the suggestions API tends to return short
          // canonical names like 'EXTRUDER: 90ADEX', while
          // getMachinesByCompany may include serial / instance
          // suffixes like 'EXTRUDER: 90ADEX - DSD (20202406)'.
          // Substring matching in either direction handles both
          // shapes without needing exact equality.
          //
          // Three pass-through cases keep this from being too
          // aggressive:
          //   (a) userMachineNames empty — loader hasn't
          //       completed (or failed). Better to show
          //       unfiltered suggestions than to silently blank
          //       the autocomplete.
          //   (b) suggestion has empty machineName — generic
          //       part with no machine association; allowed.
          //   (c) any user machine name fuzzy-matches — the
          //       intended path; let the suggestion through.
          var normStrong = function (s) {
            return String(s == null ? '' : s)
              .toLowerCase().replace(/[^a-z0-9]/g, '');
          };
          var userNames = self.userMachineNames || [];
          var hasFilter = userNames.length > 0;
          var filtered = res.filter(function (curr) {
            if (!hasFilter) return true;
            var sugName = normStrong(curr.machineName);
            if (!sugName) return true;
            return userNames.some(function (u) {
              if (!u) return false;
              return u.indexOf(sugName) !== -1
                  || sugName.indexOf(u) !== -1;
            });
          });
          if (hasFilter && filtered.length < res.length) {
            var dropped = res.filter(function (c) {
              return filtered.indexOf(c) === -1;
            }).map(function (c) { return c.machineName; });
            console.log('[Head] autocomplete filter: ' +
              (res.length - filtered.length) +
              ' of ' + res.length +
              ' suggestion(s) hidden — machineName not in ' +
              'company ' + self.selectedCompanyId +
              '. Hidden machine names:', dropped);
          }
          var i = 0;
          self.searchOption = filtered.reduce(function (acc, curr) {
            acc.push({
              key: i,
              value: curr.displayName,
              code: curr.code,
              desc: curr.description,
              assembly: curr.assembly || '',
              machineName: curr.machineName || '',
              machineId: curr.machineId || 0,
              assemblyId: curr.assemblyId || 0
            });
            i++;
            return acc;
          }, []);
        });
    },
    // ===== loadUserMachineNames =====
    // Fetch the current company's machine list and stash a
    // normalized array of machine NAMES on the component. Used
    // by getSearchedParts to filter autocomplete suggestions
    // via fuzzy name matching.
    //
    // Why name-based matching: the machineId on getPartSuggestion
    // results is the master Machine ID, but
    // getMachinesByCompany returns CompanyMachine IDs (project
    // gotcha — different ID spaces). Matching on the
    // human-readable machine label is the documented workaround
    // and is what the page-level applyPendingHeaderMachine
    // matcher does too.
    //
    // We probe text / name / machineName / displayName / value
    // because the backend's response shape varies by path.
    // Whichever populates first wins. Anything we extract is
    // alphanumeric-stripped + lowercased so case / spacing /
    // punctuation differences between the suggestion source and
    // the machines source don't cause spurious mismatches.
    //
    // Failure mode: leaves userMachineNames empty. The filter
    // treats an empty list as "no filter" so the autocomplete
    // still works (just unfiltered) — safer than blanking
    // suggestions on a transient network blip.
    loadUserMachineNames() {
      if (!this.selectedCompanyId) return;
      var self = this;
      var normStrong = function (s) {
        return String(s == null ? '' : s)
          .toLowerCase().replace(/[^a-z0-9]/g, '');
      };
      apiService.getMachinesByCompany({ id: this.selectedCompanyId })
        .then(function (data) {
          var names = [];
          (data || []).forEach(function (m) {
            var label = m.text || m.name || m.machineName ||
                        m.displayName || m.value || '';
            var n = normStrong(label);
            if (n) names.push(n);
          });
          self.userMachineNames = names;
          console.log('[Head] loaded ' + names.length +
            ' machine name(s) for autocomplete filter (company ' +
            self.selectedCompanyId + '):', names);
        })
        .catch(function (e) {
          console.warn('[Head] failed to load machines for autocomplete filter — ' +
            'suggestions will pass through unfiltered:', e);
          self.userMachineNames = [];
        });
    },
    getCompanies() {
      // Mirror an active admin preview (Master-only server-side): fetch the list AS the previewed
      // user/role, so the header shows exactly what THAT identity would see — view-as Mark Warren
      // must list NC999 + his territory customers (the same set as the report's Customer ID(s)
      // card), not the Master's full list (user 2026-08-01). Real reps send no params and are
      // scoped by their own JWT; the server ignores both params for non-Master callers.
      var _pv = {};
      if (this.viewingAsUser && this.viewingAsUser.id) _pv.viewAsUserId = this.viewingAsUser.id;
      else if (this.viewingAsRole) _pv.previewRole = this.viewingAsRole;
      feapiService.getCompanyList(_pv).then(function (data) {
        this.companies = data;

        // ===== GUARANTEE A SELECTED COMPANY (anti "all-companies" leak) =====
        // The orders / quotes / insights endpoints scope by companyId, and for
        // a Master an EMPTY companyId means "no company filter" — the backend
        // then returns every company's rows (OrderController.Get only adds the
        // CompanyId filter when companyId > 0, and applies no user filter for a
        // Master). So the dashboard's "Completed Orders" and SavedCarts' Orders
        // tab would silently show ALL companies whenever localStorage.companyId
        // is blank.
        //
        // localStorage.companyId is the single source of truth every consumer
        // reads, but it is NEVER seeded at login and was previously only
        // auto-written here when there was exactly ONE company. A multi-company
        // Master who hadn't explicitly picked a company in the header therefore
        // left it blank, which the backend reads as "all companies".
        //
        // Fix: whenever no company is selected yet, default to the FIRST
        // company in the list and persist it (selectCompany writes both
        // this.selectedCompanyId and localStorage.companyId, and emits
        // company-changed so the dashboard refetches for that company). This
        // makes the displayed company and localStorage.companyId always agree,
        // so consumers reading localStorage.companyId get a real id instead of
        // an empty value. It does NOT touch the explicit All-Companies /
        // All-Account modes elsewhere — those send an empty id intentionally.
        // Home privileged users (anyone who can SEE NC999 = JC Steele & Sons — i.e. Master / locked Sales) to
        // NC999 at the START of each browser session. localStorage.companyId is sticky across reloads AND
        // browser restarts, so a prior "act as this customer" switch (a saved-cart reorder, or opening a
        // customer's cart from Sales Insights) would otherwise leave a Master stuck on that customer's company
        // (e.g. WV115) on every future visit — the reported "I keep getting switched to that company".
        // sessionStorage clears when the browser/tab closes, so this resets to NC999 on a fresh open while
        // still letting a DELIBERATE in-session switch survive a page reload (F5). Non-privileged single-company
        // users have no NC999 in their list, so they're unaffected.
        // Email Insights "Quote this part" opens Search in a NEW TAB carrying the target CUSTOMER company in the
        // URL (switchCompanyId). Honor it: select that company and SKIP the NC999 session-home this once, so the
        // quote lands under the customer (a fresh tab would otherwise re-home to NC999). The company is normally
        // already in the rep's list (territory companies), but inject it as a fallback. Non-Customer staff only.
        var _sq = (this.$route && this.$route.query) || {};
        var _switchCoId = _sq.switchCompanyId ? String(_sq.switchCompanyId) : '';
        if (_switchCoId && this.effectiveRoleName !== 'Customer') {
          if (!data.some(function (c) { return String(c.value) === _switchCoId; })) {
            data.push({ value: _switchCoId, text: (_sq.switchCompanyName || 'Customer') + (_sq.switchCompanyCode ? ' (' + _sq.switchCompanyCode + ')' : '') });
            this.companies = data;
          }
          sessionStorage.setItem('jcsCompanyHomed', '1');
          var _switchTarget = data.find(function (c) { return String(c.value) === _switchCoId; });
          // Only switch if we're NOT already on it (main.js seeded localStorage.companyId from the URL before
          // mount, so we usually are) — a redundant selectCompany would re-fire company-changed and RESET the
          // Search page's machine/assembly selection.
          if (_switchTarget && String(this.selectedCompanyId) !== _switchCoId) this.selectCompany(_switchTarget, { silent: true });
        } else {
        var nc999 = data.find(function (c) { return /\(NC999\)/.test(c.text || ''); });
        if (nc999 && !sessionStorage.getItem('jcsCompanyHomed')) {
          sessionStorage.setItem('jcsCompanyHomed', '1');
          // carryCarts: the previous session may have ended while switched into a customer, with that
          // customer's LIVE cart pointers still loaded. Homing silently without the pointer bookkeeping
          // left them live under NC999 — the next real switch then saved them into cart_company_<NC999>,
          // clobbering the rep's own NC999 cart (adversarial review 2026-08-01). carryCarts banks the
          // old company's pointers under its own slot and restores NC999's, with no reload.
          this.selectCompany(nc999, { silent: true, carryCarts: true });
        } else if (!this.selectedCompanyId && data.length >= 1) {
          // No company chosen yet — prefer NC999 if the user can see it, else the first company.
          this.selectCompany(nc999 || data[0], { silent: true });
        } else if (this.selectedCompanyId && data.length > 0) {
          // A company id is already stored. If it isn't a company the user can
          // actually see (stale id left over from another session/account, or
          // a value that no longer exists), it would still slip past as a
          // truthy-but-bogus filter on some calls and as a blank header label.
          // Re-pin to the first valid company so the header label and the
          // stored id stay consistent and valid.
          var stillValid = data.some(function (c) {
            return String(c.value) === String(this.selectedCompanyId);
          }.bind(this));
          if (!stillValid) {
            // Prefer the NC999 home over the alphabetical first entry — this is the path a
            // preview change takes when the previously-selected company isn't in the new list.
            // carryCarts: this is a real company transition — keep the cart pointers honest too.
            this.selectCompany(nc999 || data[0], { silent: true, carryCarts: true });
          } else if (String(localStorage.companyId || '') !== String(this.selectedCompanyId)) {
            // Header shows a valid company but localStorage drifted (e.g. a
            // cart/reorder page rewrote or cleared it). Re-sync without the
            // full selectCompany reload so we don't disrupt the page.
            localStorage.companyId = this.selectedCompanyId;
          }
        }
        }
        // Keep the active company CODE in sync (covers the reload path where selectCompany isn't called) so the
        // Sales Insights report can scope by it. Both label shapes (see selectCompany): STRICT leading
        // "(CODE) -" (Sales territory / Customer lists) FIRST, then trailing "(CODE)" (Master list).
        // ⚠ Order matters: 35 real customers have NAMES ending in "(...)" — "(NC250) - GENERAL SHALE (082)"
        // must yield NC250, not 082 (adversarial review 2026-08-01). The leading match requires the ") -"
        // separator, so a Master-shape name that merely STARTS with "(" can't false-match.
        var _sel = this.companies.find(function (c) { return String(c.value) === String(this.selectedCompanyId); }.bind(this));
        var _m = _sel ? ((_sel.text || '').match(/^\s*\(([^)]+)\)\s*-/) || (_sel.text || '').match(/\(([^)]+)\)\s*$/)) : null;
        localStorage.companyCode = _m ? _m[1].trim() : '';
        // A header pick reloads the page, and after the reload selectedCompanyId never changes — so the
        // selectedCompanyId watcher (the only other loadRepContacts call site) never fires and the rep row's
        // customer-contact picker stayed empty. Load it here, now that companyCode is correct; the method
        // no-ops for NC999 / Customer role on its own.
        this.loadRepContacts();
      }.bind(this)).catch(function () {
        this.companies = [];
      }.bind(this));
    },
    loadCartCount() {
  var self = this;
  var cartId = localStorage.cartId;
  var cartIdRFQ = localStorage.cartIdRFQ;

  function isValidCartId(id) {
    return id && parseInt(id) > 0;
  }

  if (!localStorage.cartLastActivity) {
    if (isValidCartId(cartId)) {
      localStorage.cartLastActivity = Date.now().toString();
    } else if (isValidCartId(cartIdRFQ)) {
      localStorage.cartLastActivity = Date.now().toString();
    } else {
      self.cartItemCount = 0;
      return;
    }
  }

  cartId = localStorage.cartId;
  cartIdRFQ = localStorage.cartIdRFQ;

  if (isValidCartId(cartId) || isValidCartId(cartIdRFQ)) {
    var totalCount = 0;
    var pending = 0;
    // Track per-side counts independently so each header link
    // shows the badge for ITS OWN content. Aggregating into a
    // single total used to be confusing — the badge could come
    // from either side, but the link only went to one. Now each
    // link inside the consolidated View Cart entry (Orders /
    // Quotes) has its own count and its own destination.
    var orderCount = 0;
    var quoteCount = 0;

    function countCart(id, storageKey) {
      if (isValidCartId(id)) {
        pending++;
        feapiService.getCart({ id: id }).then(function (cart) {
          var count = 0;
          if (cart && cart.purchaseOrders) {
            // Count distinct PO Numbers (Orders) on the cart —
            // matches the "ORDER #1 / ORDER #2 …" headings the
            // user sees on the staging panel and on /cart. Two
            // different machines + assemblies under the SAME
            // PO Number are still ONE Order, not two.
            //
            // Why we changed this twice:
            //   1. Original: po.parts.length summed — inflated
            //      for partial save/place flows that produce
            //      multiple PurchaseOrder rows per assembly.
            //   2. Then: unique PO+machine+assembly triples —
            //      counted assembly cards rather than Orders,
            //      so a cart with 1 PO covering 2 assemblies
            //      showed a badge of 2 even though the user
            //      sees a single "Order #1" group.
            //   3. Now (correct): unique PO Numbers — matches
            //      what the user reads as "Orders" on the page.
            //
            // PO-less rows (po.number empty/null) are skipped —
            // they don't render as a numbered Order in the UI
            // and shouldn't pad the badge. They're rare in
            // practice (a transient state during reorder /
            // edit) but skipping them keeps the badge stable
            // through those flows.
            var seenPOs = {};
            cart.purchaseOrders.forEach(function (po) {
              var poNumber = po && po.number != null ? String(po.number).trim() : "";
              if (!poNumber) return;
              if (!seenPOs[poNumber]) {
                seenPOs[poNumber] = true;
                count++;
              }
            });
          }

          if (count === 0 && storageKey) {
            delete localStorage[storageKey];
          }

          // Bucket the count into the right side so we can update
          // each header badge independently.
          if (storageKey === 'cartId') {
            orderCount = count;
          } else if (storageKey === 'cartIdRFQ') {
            quoteCount = count;
          }
          totalCount += count;
          pending--;

          if (pending === 0) {
            self.cartItemCount = orderCount;
            self.quoteItemCount = quoteCount;
            if (totalCount > 0) {
              self.startCartExpiryTimer();
            } else {
              delete localStorage.cartLastActivity;
            }
          }
        }).catch(function () {
          if (storageKey) {
            delete localStorage[storageKey];
          }
          pending--;
          if (pending === 0) {
            self.cartItemCount = orderCount;
            self.quoteItemCount = quoteCount;
            if (totalCount <= 0) {
              delete localStorage.cartLastActivity;
            }
          }
        });
      }
    }

    countCart(cartId, 'cartId');
    countCart(cartIdRFQ, 'cartIdRFQ');

    if (pending === 0) {
      self.cartItemCount = 0;
      self.quoteItemCount = 0;
    }
  } else {
    self.cartItemCount = 0;
    self.quoteItemCount = 0;
    delete localStorage.cartLastActivity;
  }
},
startCartExpiryTimer() {
  if (this.cartWarningTimer) {
    clearTimeout(this.cartWarningTimer);
    this.cartWarningTimer = null;
  }

  if (this.expiryCountdownInterval) {
    clearInterval(this.expiryCountdownInterval);
    this.expiryCountdownInterval = null;
  }

  const ONE_MINUTE = 60 * 1000;
  const ONE_HOUR = 60 * ONE_MINUTE;
  const totalTime = 48 * ONE_HOUR;

  if (!localStorage.cartLastActivity) {
    localStorage.cartLastActivity = Date.now().toString();
  }

  var lastActivity = parseInt(localStorage.cartLastActivity || '0');
  var elapsed = Date.now() - lastActivity;

  if (elapsed >= totalTime) {
    this.$nextTick(function () {
      this.expireCart();
    }.bind(this));
  } else {
    var remaining = totalTime - elapsed;
    this.cartWarningTimer = setTimeout(function () {
      this.expireCart();
    }.bind(this), remaining);
  }
},

showCartExpiryWarning() {
  return;
},
// ===== handleHeaderSearchCleared =====
// Triggered when a Search / Order / RFQ page's clearHeaderSearch
// (the × button on the SEARCHING FOR banner) emits the
// 'cleared' event on $headerSearchBus.
//
// Resets every Head.vue-owned piece of state tied to the search
// input so the input field, the autocomplete dropdown, and the
// persisted search context all clear together. Without this,
// dismissing the banner on a page would leave the dismissed
// term still visible in the header input — confusing UX.
//
// State reset:
//   - searchTerm — the input field's v-model
//   - searchOption — suggestion dropdown items
//   - showCustomDropdown — visibility flag for the dropdown
//   - code — last-picked part code from a suggestion
//   - description — last-picked part description
//   - localStorage.searchTerm + .description (Head.vue's
//     persistence backing)
//
// localStorage.headerSearchParams is wiped by the page-level
// clearHeaderSearch already; not duplicated here.
handleHeaderSearchCleared() {
  this.searchTerm = "";
  this.searchOption = [];
  this.showCustomDropdown = false;
  this.code = "";
  this.description = "";
  this.selectedAssemblyId = 0;
  try { delete localStorage.searchTerm; } catch (e) {}
  try { delete localStorage.description; } catch (e) {}
},
handleCartUpdated() {
  this.markCartActivity();
  this.loadCartCount();
},

markCartActivity() {
  localStorage.cartLastActivity = Date.now().toString();

  if (this.cartWarningTimer) {
    clearTimeout(this.cartWarningTimer);
    this.cartWarningTimer = null;
  }

  if (this.expiryCountdownInterval) {
    clearInterval(this.expiryCountdownInterval);
    this.expiryCountdownInterval = null;
  }

  this.showExpiryWarning = false;
},
    continueShopping() {
  this.showExpiryWarning = false;

  if (this.expiryCountdownInterval) {
    clearInterval(this.expiryCountdownInterval);
    this.expiryCountdownInterval = null;
  }

  this.markCartActivity();
  this.startCartExpiryTimer();
},
    saveCartNow() {
  this.showExpiryWarning = false;

  if (this.expiryCountdownInterval) {
    clearInterval(this.expiryCountdownInterval);
    this.expiryCountdownInterval = null;
  }

  this.expireCart();
},
    convertToQuoteFromModal() {
  this.showExpiryWarning = false;

  if (this.expiryCountdownInterval) {
    clearInterval(this.expiryCountdownInterval);
    this.expiryCountdownInterval = null;
  }

  if (this.cartWarningTimer) {
    clearTimeout(this.cartWarningTimer);
    this.cartWarningTimer = null;
  }

  this.markCartActivity();

  var currentRoute = this.$route.name || '';
  if (currentRoute === 'Cart' || this.$route.path === '/cart') {
    return;
  }

  window.location.href = '/cart';
},
    placeOrderFromModal() {
  this.showExpiryWarning = false;

  if (this.expiryCountdownInterval) {
    clearInterval(this.expiryCountdownInterval);
    this.expiryCountdownInterval = null;
  }

  if (this.cartWarningTimer) {
    clearTimeout(this.cartWarningTimer);
    this.cartWarningTimer = null;
  }

  this.markCartActivity();

  var currentRoute = this.$route.name || '';
  if (currentRoute === 'Cart' || this.$route.path === '/cart') {
    return;
  }

  window.location.href = '/cart';
},
    expireCart() {
      var companyId = localStorage.companyId;

      if ((!localStorage.cartId || localStorage.cartId <= 0) &&
          (!localStorage.cartIdRFQ || localStorage.cartIdRFQ <= 0)) {
        return;
      }

      if (companyId) {
        var saveKey = 'cart_company_' + companyId;
        var stateToSave = {
          cartId: localStorage.cartId || '',
          cartIdRFQ: localStorage.cartIdRFQ || '',
          poNumber: localStorage.poNumber || '',
          poNumberRFQ: localStorage.poNumberRFQ || '',
          machineId: localStorage.machineId || '',
          machineIdRFQ: localStorage.machineIdRFQ || '',
          assemblyId: localStorage.assemblyId || '',
          assemblyIdRFQ: localStorage.assemblyIdRFQ || ''
        };
        localStorage.setItem(saveKey, JSON.stringify(stateToSave));
      }

      delete localStorage.cartId;
      delete localStorage.cartIdRFQ;
      delete localStorage.poNumber;
      delete localStorage.poNumberRFQ;
      delete localStorage.machineId;
      delete localStorage.machineIdRFQ;
      delete localStorage.assemblyId;
      delete localStorage.assemblyIdRFQ;
      delete localStorage.cartLastActivity;
      localStorage.cartExpired = 'true';

      this.cartItemCount = 0;
      this.showSavingOverlay = true;

      var self = this;
      var currentRoute = this.$route.name || '';
      var cartPages = ['Cart', 'Quote Saved Items', 'Saved Quote', 'Finalize Cart'];
      var isCartPage = cartPages.indexOf(currentRoute) !== -1;

      setTimeout(function () {
        if (isCartPage) {
          window.location.reload();
        } else {
          self.showSavingOverlay = false;
        }
      }, 2000);
    },
    toggleCompanyDropdown() {
      this.showCompanyDropdown = !this.showCompanyDropdown;
      if (this.showCompanyDropdown) {
        this.companySearchTerm = '';
        this.$nextTick(function () {
          if (this.$refs.companySearchInput) {
            this.$refs.companySearchInput.focus();
          }
        }.bind(this));
      }
    },
    // Header search (reporting roles) selected a filter suggestion → drive the report.
    onHeaderFilterSelect(item) {
      if (!item || !item.filterField) return;
      if (this.$route && this.$route.path === '/reports/customer-insights') {
        this.$root.$emit('report-filter-select', item);
      } else {
        var q = {}; q[item.filterField] = item.filterValue;
        this.$router.push({ path: '/reports/customer-insights', query: q }).catch(function () {});
      }
    },
    // Header date shortcut: today/yesterday/week/month, a quarter (q1-q4), YTD, or N calendar years (1-5 = Jan 1 … Dec 31).
    setReportRange(kind) {
      var now = new Date(); var y = now.getFullYear();
      var iso = function (d) { return d.toISOString().slice(0, 10); };
      var from, to = iso(now);
      if (kind === 'today') { from = iso(now); }
      else if (kind === 'yesterday') { var yd = new Date(now.getTime()); yd.setDate(now.getDate() - 1); from = iso(yd); to = from; }
      else if (kind === 'week') { var w = new Date(now.getTime()); w.setDate(now.getDate() - 6); from = iso(w); }
      else if (kind === 'month') { var mo = new Date(now.getTime()); mo.setMonth(now.getMonth() - 1); from = iso(mo); }
      else if (kind === 'q1') { from = y + '-01-01'; to = y + '-03-31'; }
      else if (kind === 'q2') { from = y + '-04-01'; to = y + '-06-30'; }
      else if (kind === 'q3') { from = y + '-07-01'; to = y + '-09-30'; }
      else if (kind === 'q4') { from = y + '-10-01'; to = y + '-12-31'; }
      else if (kind === 'ytd') { from = y + '-01-01'; }
      else { var n = Number(kind) || 1; from = (y - n + 1) + '-01-01'; to = y + '-12-31'; }
      this.emitReportRange(from, to);
    },
    // Deliver a date window to the Sales Insights report (or navigate to it if we're not there yet).
    // Shared by the shortcut buttons AND the compact From/To pickers.
    emitReportRange(from, to) {
      if (this.$route && this.$route.path === '/reports/customer-insights') {
        this.$root.$emit('report-date-range', { from: from, to: to });
      } else {
        this.$router.push({ path: '/reports/customer-insights', query: { from: from, to: to } }).catch(function () {});
      }
    },
    // Header From/To pickers changed → update the local mirror, then push the window once both ends are set.
    onHeaderPickDate(which, ev) {
      var val = (ev && ev.target) ? ev.target.value : '';
      if (which === 'from') this.rptFrom = val; else this.rptTo = val;
      if (!this.rptFrom || !this.rptTo) return;            // need both ends to define a range
      var from = this.rptFrom, to = this.rptTo;
      if (from > to) { from = this.rptTo; to = this.rptFrom; this.rptFrom = from; this.rptTo = to; }  // swap if inverted
      this.emitReportRange(from, to);
    },
    // The report broadcasts its active window (initial load, shortcut, drawer, or card click) so the
    // header pickers always reflect it.
    onReportDateCurrent(payload) {
      if (!payload) return;
      if (payload.from) this.rptFrom = payload.from;
      if (payload.to) this.rptTo = payload.to;
    },
    // Sales Insights broadcasts its currently-selected customer (exactly one selected
    // → that customer; anything else → cleared). Stored so the Sales-rep parts-request
    // contact modal can prefill the CUSTOMER company. Frontend-only, not persisted.
    onReportCustomerContext(payload) {
      if (payload && payload.customerName) {
        this.reportCustomer = {
          customerId: payload.customerId != null ? String(payload.customerId) : "",
          customerName: String(payload.customerName),
          machineText: payload.machineText || "",
          partText: payload.partText || "",
          partCode: payload.partCode || "",
          summary: payload.summary || "",
          avgQty: payload.avgQty || ""
        };
      } else {
        this.reportCustomer = { customerId: "", customerName: "", machineText: "", partText: "", partCode: "", summary: "", avgQty: "" };
      }
    },
    // Return @jcsteele.com / non-Customer staff to their NC999 home after an order/quote (they may have switched
    // into a customer to quote for them). Silent — no reload — but carryCarts so the customer's cart pointers
    // are banked under their own slot and the rep's NC999 pointers come back (previously the customer's stayed
    // live under NC999 — adversarial review 2026-08-01). Customers stay on their own company.
    returnHomeToNC999() {
      if (!this.currentUser || this.effectiveRoleName === 'Customer') return;
      var nc999 = (this.companies || []).find(function (c) { return /\(NC999\)/.test(c.text || ''); });
      if (nc999 && String(this.selectedCompanyId) !== String(nc999.value)) {
        this.selectCompany(nc999, { silent: true, carryCarts: true });
      }
    },
    // The rep-row "← Back to my view" BUTTON: a FULL (non-silent) return home. The rep got here via a
    // full header pick, so the way back must also save/restore cart pointers AND reload — a silent
    // return left the open page showing the customer's data under an NC999 header (adversarial review
    // 2026-08-01). The event-driven returnHomeToNC999 above stays silent for the post-order/quote flow
    // (jcs-return-home fires on success pages where a reload would be disruptive).
    repReturnHome() {
      if (!this.currentUser || this.effectiveRoleName === 'Customer') return;
      var nc999 = (this.companies || []).find(function (c) { return /\(NC999\)/.test(c.text || ''); });
      if (nc999 && String(this.selectedCompanyId) !== String(nc999.value)) {
        this.selectCompany(nc999, { silent: false });
      }
    },
    // Report-driven silent company switch (Customer Insights → "view as customer").
    // The target customer's company may not be in this user's own company list,
    // so inject it before selecting; selectCompany(silent) updates the header +
    // cart context without a reload, so the report keeps its state.
    onReportCompanySwitch(payload) {
      if (!payload || !payload.value) return;
      var val = String(payload.value);
      var txt = payload.text || '';
      if (String(this.selectedCompanyId) === val) return; // already active
      var exists = this.companies.some(function (c) { return String(c.value) === val; });
      if (!exists) {
        this.companies = [{ value: val, text: txt }].concat(this.companies);
      }
      // reload flag (from the report's "open a customer's cart" flow) forces a full,
      // non-silent switch so the cart pointers save/restore + the page reloads into the
      // company. Without it (e.g. the Sales filter-switch) the switch stays silent —
      // carryCarts (sent by the report's syncCompany) keeps the cart pointers honest
      // on those silent switches without the reload.
      this.selectCompany({ value: val, text: txt }, { silent: !payload.reload, carryCarts: !!payload.carryCarts });
    },
    closeCompanyDropdown() {
      this.showCompanyDropdown = false;
      this.companySearchTerm = '';
    },
selectCompany(company, opts) {
  // opts.silent === true: establish the selection WITHOUT the
  // window.location.reload() at the end. Used when we're DEFAULTING the
  // company for a user who had none selected yet (see getCompanies). In
  // that case there's no outgoing company whose cart needs preserving and
  // no reason to bounce the page — the company-changed event below already
  // makes the dashboard refetch, and every other page reads
  // localStorage.companyId fresh on its own mount. A reload here would also
  // be jarring on first load. Explicit user picks from the dropdown still
  // pass no opts, so they keep the existing save/restore + reload behavior.
  opts = opts || {};
  var previousCompanyId = this.selectedCompanyId;
  this.selectedCompanyId = company.value;
  localStorage.companyId = company.value;
  // Also stash the company CODE so company-scoped views — the Sales Insights report — can filter by
  // it without re-deriving from the numeric id. Two label shapes exist: "(CODE) - Name" (the Sales
  // territory list and the Customer linked-companies list) and "Name - (CODE)" (Master / Sales All).
  // ⚠ STRICT leading "(CODE) -" must be tried FIRST: 35 real customers have NAMES ending in "(...)" —
  // "(NC250) - GENERAL SHALE (082)" must yield NC250, not 082 (adversarial review 2026-08-01). The
  // required ") -" separator keeps a Master-shape name that merely starts with "(" from false-matching.
  var _cc = (company.text || '').match(/^\s*\(([^)]+)\)\s*-/) || (company.text || '').match(/\(([^)]+)\)\s*$/);
  localStorage.companyCode = _cc ? _cc[1].trim() : '';
  // Broadcast the company change on the shared $cartBus BEFORE the
  // cart-state save/restore + reload below. Views that show company-scoped
  // data (e.g. the Account dashboard's Completed Orders / Quotes / insights)
  // listen for this and refetch immediately for the newly-selected company.
  // Emitting here — ahead of the cart save/restore block — guarantees the
  // signal fires even if that block throws (a corrupt cart_company_* JSON
  // entry) and the window.location.reload() at the end is skipped; in that
  // case the listening view is the only thing that refreshes the dashboard,
  // so it must not depend on the reload. (When the reload DOES fire it simply
  // remounts everything fresh and the event is moot — harmless either way.)
  if (previousCompanyId !== company.value && this.$cartBus) {
    this.$cartBus.$emit('company-changed', String(company.value || ''));
  }
  // Refresh the autocomplete's company-machine filter for
  // the newly-selected company. Fires on every company change
  // (not just the first) so the filter never serves stale
  // machine names from a previous company.
  this.loadUserMachineNames();
  this.showCompanyDropdown = false;
  this.companySearchTerm = '';

  // Cart-pointer bookkeeping runs for every REAL transition that should carry cart state: all
  // non-silent (user) picks, plus silent switches that pass carryCarts (session NC999 homing,
  // report-driven switches, return-home). Without it a silent switch left the OLD company's live
  // cart pointers under the NEW company — the next non-silent pick then banked them into the wrong
  // cart_company_ slot, cross-linking carts (adversarial review 2026-08-01).
  if ((!opts.silent || opts.carryCarts) && previousCompanyId !== company.value) {
    if (previousCompanyId) {
      var saveKey = 'cart_company_' + previousCompanyId;
      var stateToSave = {
        cartId: localStorage.cartId || '',
        cartIdRFQ: localStorage.cartIdRFQ || '',
        poNumber: localStorage.poNumber || '',
        poNumberRFQ: localStorage.poNumberRFQ || '',
        machineId: localStorage.machineId || '',
        machineIdRFQ: localStorage.machineIdRFQ || '',
        assemblyId: localStorage.assemblyId || '',
        assemblyIdRFQ: localStorage.assemblyIdRFQ || ''
      };
      localStorage.setItem(saveKey, JSON.stringify(stateToSave));
    }

    var restoreKey = 'cart_company_' + company.value;
    var saved = localStorage.getItem(restoreKey);

    if (saved) {
      var restored = JSON.parse(saved);
      localStorage.cartId = restored.cartId || '';
      localStorage.cartIdRFQ = restored.cartIdRFQ || '';
      localStorage.poNumber = restored.poNumber || '';
      localStorage.poNumberRFQ = restored.poNumberRFQ || '';
      localStorage.machineId = restored.machineId || '';
      localStorage.machineIdRFQ = restored.machineIdRFQ || '';
      localStorage.assemblyId = restored.assemblyId || '';
      localStorage.assemblyIdRFQ = restored.assemblyIdRFQ || '';
    } else {
      delete localStorage.cartId;
      delete localStorage.cartIdRFQ;
      delete localStorage.poNumber;
      delete localStorage.poNumberRFQ;
      delete localStorage.machineId;
      delete localStorage.machineIdRFQ;
      delete localStorage.assemblyId;
      delete localStorage.assemblyIdRFQ;
    }

    if (!opts.silent) {
      // A deliberate pick must WIN over any switchCompanyId still sitting in the URL (the Email
      // Insights "Quote this part" tabs carry one): main.js re-seeds localStorage.companyId from it
      // synchronously on every load, which silently reverted the pick after the reload below while
      // the freshly-restored cart pointers stayed — cart contamination (adversarial review
      // 2026-08-01). Drop the stale params so the reload honors what the user chose.
      try {
        if (window.location.search && /[?&]switchCompanyId=/.test(window.location.search)) {
          var _u = new URL(window.location.href);
          _u.searchParams.delete('switchCompanyId');
          _u.searchParams.delete('switchCompanyCode');
          _u.searchParams.delete('switchCompanyName');
          window.history.replaceState(null, '', _u.toString());
        }
      } catch (e) { /* URL API missing → keep legacy behavior */ }
      this.$nextTick(function () {
        window.location.reload();
      });
    } else {
      // Silent carryCarts switch: no reload — refresh the cart badge for the restored pointers.
      this.loadCartCount();
    }
  }
},
    // Fetch the role names defined in the database for the admin-only "view as
    // role" dropdown. Master-only endpoint — only ever called for real admins,
    // whose real token authorizes it. The admin's own role is always kept in
    // the list (so they can switch back), and the list survives a failed call
    // by at least offering Customer.
    loadRoleOptions() {
      if (!this.isRealAdmin) return;
      var self = this;
      // Seed with the real role so the dropdown is never empty before the fetch.
      if (this.realRoleName && this.roleOptions.indexOf(this.realRoleName) === -1) {
        this.roleOptions = [this.realRoleName];
      }
      apiService
        .getUserTypes()
        .then(function (data) {
          var names = (data || [])
            .map(function (r) {
              return r.text || r.Text || r.name || r.Name || "";
            })
            .filter(Boolean);
          var seen = {};
          var opts = [];
          names.forEach(function (n) {
            var k = n.toLowerCase();
            if (!seen[k]) {
              seen[k] = true;
              opts.push(n);
            }
          });
          // Guarantee the admin's real role is present (so "switch back" works
          // even if it were ever missing from the returned list).
          if (self.realRoleName && !seen[self.realRoleName.toLowerCase()]) {
            opts.unshift(self.realRoleName);
          }
          self.roleOptions = opts;
        })
        .catch(function () {
          var opts = self.realRoleName ? [self.realRoleName] : [];
          if (opts.indexOf("Customer") === -1) opts.push("Customer");
          self.roleOptions = opts;
        });
    },
    // Admin picked a role from the dropdown. Selecting their own role clears
    // the preview; any other role previews the portal as that role. Purely a
    // frontend override (the real token is untouched), so it applies reactively
    // — the currentUser getter recomputes and every admin-gated block across
    // the portal shows/hides immediately, no reload needed.
    onRoleChange(role) {
      if (!role || role === this.realRoleName) {
        this.$store.dispatch("setViewAsRole", "");
      } else {
        this.$store.dispatch("setViewAsRole", role);
      }
    },
    // Quick "back to my role" affordance shown while previewing. Also returns to the admin's
    // home company (NC999) — not whatever customer they browsed into from the Sales Insights
    // report — so "Back to my view" fully restores their own Master + NC999 view.
    resetRole() {
      this.$store.dispatch("setViewAsRole", "");   // clears viewAsUser too (see the store action)
      var home = (this.companies || []).find(function (c) {
        return String(c.text || "").indexOf("NC999") >= 0;
      });
      if (home && String(home.value) !== String(this.selectedCompanyId)) {
        // Non-silent → saves/restores the per-company cart pointers and reloads into NC999.
        this.selectCompany({ value: home.value, text: home.text }, { silent: false });
      }
    },
    // Load the selected company's users (admin-only) for the "View as user" dropdown. Empty if the endpoint isn't deployed.
    loadCompanyUsers() {
      if (!this.isRealAdmin || !this.selectedCompanyId) { this.companyUsers = []; return; }
      var self = this;
      apiService.getCompanyUsers(this.selectedCompanyId)
        .then(function (res) { self.companyUsers = (res && res.users) ? res.users : []; })
        .catch(function () { self.companyUsers = []; });
    },
    userLabel(u) {
      var nm = (((u.firstName || "") + " " + (u.lastName || "")).trim()) || u.email || "User";
      return u.role ? (nm + " · " + u.role) : nm;
    },
    // Admin picked a user → preview as them (their role + their personal report layouts). Empty = back to my own view.
    onUserChange(userId) {
      if (!userId) { this.$store.dispatch("setViewAsUser", null); return; }
      var u = (this.companyUsers || []).find(function (x) { return String(x.id) === String(userId); });
      if (!u) return;
      var nm = (((u.firstName || "") + " " + (u.lastName || "")).trim()) || u.email;
      this.$store.dispatch("setViewAsUser", { id: u.id, name: nm, role: u.role });
      // Impersonation also PROVISIONS (user 2026-07-31): grant whatever this user's Territory/Report
      // Access rows already entitle them to, NOW, instead of waiting for their next Microsoft sign-in.
      // Server-side it is the exact sign-in routine — strictly additive, idempotent, Master-gated —
      // so previewing someone can never take anything away. Fire-and-forget: the preview works either way.
      var self = this;
      feapiService.provisionUser(u.id)
        .then(function (res) {
          var d = (res && res.data) || res || {};
          if (d.ok && d.added && d.added.length) {
            self.loadCompanyUsers();   // refresh the dropdown's role labels
            window.alert(nm + " was provisioned: " + d.added.join(", ") + ".\n\nActive immediately for this preview and at their next sign-in (an existing session keeps its old token for up to 24 hours).");
          }
        })
        .catch(function () { /* the preview still works; provisioning is a bonus */ });
    },
    // × button on the header search: clear the field, the remembered term, and
    // tell the Search page to drop its active header-search filter + reset.
    clearSearch() {
      this.searchTerm = "";
      this.description = "";
      delete localStorage.searchTerm;
      delete localStorage.description;
      this.showCustomDropdown = false;
      this.searchOption = [];
      try {
        Vue.prototype.$headerSearchBus = Vue.prototype.$headerSearchBus || new Vue();
        Vue.prototype.$headerSearchBus.$emit("header-cleared");
      } catch (e) { /* no-op */ }
    },
    handleLogout() {
      var companyId = localStorage.companyId;
      if (companyId) {
        var saveKey = 'cart_company_' + companyId;
        var stateToSave = {
          cartId: localStorage.cartId || '',
          cartIdRFQ: localStorage.cartIdRFQ || '',
          poNumber: localStorage.poNumber || '',
          poNumberRFQ: localStorage.poNumberRFQ || '',
          machineId: localStorage.machineId || '',
          machineIdRFQ: localStorage.machineIdRFQ || '',
          assemblyId: localStorage.assemblyId || '',
          assemblyIdRFQ: localStorage.assemblyIdRFQ || ''
        };
        localStorage.setItem(saveKey, JSON.stringify(stateToSave));
      }

      delete localStorage.cartId;
      delete localStorage.cartIdRFQ;
      delete localStorage.poNumber;
      delete localStorage.poNumberRFQ;
      delete localStorage.machineId;
      delete localStorage.machineIdRFQ;
      delete localStorage.assemblyId;
      delete localStorage.assemblyIdRFQ;
      delete localStorage.companyId;

      window.location.href = '/logout';
    }
  },
};
</script>

<style scoped>
.company-selector-wrapper {
  position: relative;
  margin-bottom: 6px;
}

/* Right-anchored container that holds the greeting + company selector
   together. Uses display: inline-block + float:right (via margin-left:auto
   in flex parent or text-align:right on parent) so it shrinks to the width
   of its widest child (the company name). Both inner lines then left-align
   inside it, sharing the same left edge. */
.account-identity {
  display: inline-block;
  margin-left: auto;
  text-align: left;
}

/* The .account-cont column is the parent — right-align its inline content
   so .account-identity floats to the right edge of the column. */
.account-cont {
  text-align: right;
}

/* When the company selector sits in the account-cont area (top-right of
   the header, above the phone/cart/account row), keep it left-aligned
   inside the .account-identity wrapper so it sits flush with the
   greeting line above. */
.company-selector-wrapper.company-selector-top {
  text-align: left;
  margin-bottom: 2px;
  margin-top: 0;
}

.company-selector-wrapper.company-selector-top .company-dropdown {
  right: 0;
  left: auto;
}

/* User greeting — left-aligned within the .account-identity wrapper so
   "Hi, Jessica" sits flush with the start of the company name below. */
.user-greeting {
  text-align: left;
  font-size: 12px;
  color: #666;
  margin-top: 0;
  margin-bottom: 0;
  letter-spacing: 0.2px;
  line-height: 1.4;
}

.user-greeting-name {
  color: #c0392b;
  font-weight: 700;
}

/* ===== Admin-only role line + "View as customer" switch =====
   Sits just under the company selector in the top-right identity stack.
   Left-aligned (flush with the greeting/company) via the inherited
   text-align:left, and inline-flex so it hugs its content. */
.header-role-line {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 1px;
  font-size: 11px;
  line-height: 1.15;
  letter-spacing: 0.2px;
}
.header-role-label {
  color: #888;
}
/* Compact role dropdown — brand-red, native arrow kept for affordance. */
.header-role-select {
  font-size: 11px;
  font-weight: 700;
  color: #c0392b;
  background: #fff;
  border: 1px solid #d6d6d6;
  border-radius: 4px;
  padding: 1px 6px;
  line-height: 1.4;
  cursor: pointer;
  max-width: 160px;
}
.header-role-select:focus {
  outline: none;
  border-color: #c0392b;
}
/* Link-style "back to my role" button — no chrome, brand-red, underlined on
   hover. Only shown while previewing. */
.header-role-switch {
  border: 0;
  background: none;
  padding: 0;
  margin-left: 2px;
  font-size: 11px;
  font-weight: 600;
  color: #a02b1f;
  cursor: pointer;
  text-decoration: none;
}
.header-role-switch:hover {
  text-decoration: underline;
}
/* PREVIEW state — an amber pill so the admin can't miss that they're
   currently seeing the portal as another role. */
.header-role-line.is-preview {
  background: #fff4e0;
  border: 1px solid #f0c36d;
  border-radius: 4px;
  padding: 0 8px;
  margin-top: 1px;
}
.header-role-line.is-preview .header-role-select {
  color: #b9770e;
  border-color: #f0c36d;
}

/* Lift the logo column up slightly so it sits closer to the top of the
   header, matching the compact greeting/company/utility stack on the
   right. Small positive value fine-tunes vertical alignment. */
.logo-cont {
  margin-top: 6px;
  /* overflow visible (the default) is important: the logo is
     translated 130px to the right via transform on #logo below, and
     at its larger 220px width it extends past the col-lg-2 column's
     right edge into the empty .search-cont column next to it. */
  overflow: visible;
}

/* Push the logo a small distance inward from the left rail.
   Earlier this used a 130px translateX (~1.5 inches inset)
   but the user asked for the logo moved further left so it
   hugs the rail more closely; 30px keeps the logo just
   barely off the rail without touching the page edge. Using
   transform rather than padding/margin keeps the column's
   bounding box the same — the header row layout
   (search-cont / account-cont proportions) isn't shifted by
   the logo move. */
.logo-cont #logo {
  display: inline-block;
  transform: translateX(30px);
}

/* Larger logo. Original SVG was rendering at its small intrinsic
   size; 220px width is ~50% bigger and reads better against the
   wider header. max-width:none defeats any global img-fluid /
   max-width:100% rule that would otherwise cap it at the column
   width. */
.logo-cont #logo img {
  width: 220px;
  height: auto;
  max-width: none;
}

/* The search bar moved out of this column to a fixed overlay on the
   breadcrumb row (#header-search). The column is preserved as an empty
   5-wide spacer to keep logo-cont and account-cont in their original
   positions; no padding needed since there's nothing inside. */
.search-cont {
  /* intentionally empty */
}

.company-selector {
  display: inline-flex;
  align-items: center;
  padding: 2px 0;
  user-select: none;
}

.company-selector.clickable {
  cursor: pointer;
}

.company-selector-label {
  font-size: 14px;
  font-weight: 600;
  color: #c0392b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 280px;
}

.company-selector-arrow {
  font-size: 10px;
  color: #c0392b;
  margin-left: 5px;
  transition: transform 0.2s;
  line-height: 1;
}

.company-selector-arrow.open {
  transform: rotate(180deg);
}

.company-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  min-width: 260px;
  max-width: 360px;
  z-index: 11;
  background: white;
  border: 1px solid #ddd;
  border-radius: 4px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  max-height: 200px;
  overflow-y: auto;
}

/* Selected-company header line — the first row of the dropdown. Hidden on
   desktop (the selector already shows the name inline there); revealed on
   mobile, where the trigger is an icon only, so the menu opens with the
   current pick confirmed at the top. */
.company-dropdown-selected {
  display: none;
  padding: 9px 12px;
  font-size: 13px;
  font-weight: 700;
  color: #13294b;
  background: #f4f6f9;
  border-bottom: 1px solid #e2e6ea;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.company-dropdown-option {
  padding: 8px 12px;
  cursor: pointer;
  font-size: 13px;
  color: #333;
  transition: background-color 0.2s;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.company-dropdown-option:hover {
  background-color: #f5f5f5;
}

.company-dropdown-option.active {
  background-color: #eef2f7;
  font-weight: 600;
}

.company-search-wrapper {
  padding: 6px 8px;
  border-bottom: 1px solid #ddd;
  position: sticky;
  top: 0;
  background: white;
}

.company-search-field {
  position: relative;
}
.company-search-icon {
  position: absolute;
  left: 8px;
  top: 50%;
  transform: translateY(-50%);
  color: #c0392b;
  pointer-events: none;
}
.company-search-input {
  width: 100%;
  /* Extra left padding leaves room for the magnifying-glass icon. */
  padding: 5px 8px 5px 26px;
  font-size: 13px;
  border: 1px solid #ddd;
  border-radius: 3px;
  outline: none;
}

.company-search-input:focus {
  border-color: #c0392b;
}

.company-closed-toggle {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  margin-top: 5px;
  margin-bottom: 0;
  cursor: pointer;
  font-size: 12px;
  color: #888;
  user-select: none;
}

.company-closed-toggle input[type="checkbox"] {
  margin: 0;
  cursor: pointer;
}

.company-closed-toggle span {
  line-height: 1;
}

.company-dropdown-empty {
  padding: 10px 12px;
  font-size: 13px;
  color: #999;
  text-align: center;
}

/* ===== HEADER CONTACT BLOCK =====
   Sits in the column that used to be the search-bar spacer
   (between logo and identity block). Centers a small two-line
   stack of contact details: phone on top, parts email below.
   Both are clickable so a tap on mobile opens the dialer or
   default mail client. Sized small enough to feel like
   secondary brand info, not a primary CTA. */
.contact-cont {
  display: flex;
  align-items: center;
  justify-content: center;
}

/* On desktop (≥lg breakpoint), the contact block needs to be
   visually centered on the PAGE, not just the column. The
   col-lg-5 wrapping it has Bootstrap's default position:
   relative — which means an absolutely-positioned child
   centers within the column (37.5% of page), not within the
   row (50% of page). Forcing the column to position: static
   makes the closest positioned ancestor the .row (which has
   inline position:relative), and absolute positioning on
   .header-contact then truly centers on the page.
   !important is required because Bootstrap's .col / .col-lg-5
   selectors are more specific than our .contact-cont. */
@media (min-width: 992px) {
  .contact-cont {
    position: static !important;
  }
  .header-contact {
    position: absolute;
    /* Pushed down a bit from top: 4px so the phone/email
       block sits visually centered with the logo on its
       vertical midline rather than hugging the top edge. */
    left: 50%;
    top: 14px;
    transform: translateX(-50%);
    /* Above the surrounding columns so a stray click on the
       phone/email link isn't intercepted by an empty column
       overlapping it. */
    z-index: 2;
  }
}

.header-contact {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  line-height: 1.3;
  padding-top: 4px;
}

/* Mobile contact icon — hidden by default (desktop shows the phone/email
   text); revealed in the mobile header @media below. Navy circle + white
   phone handset, sized to match the company/account icons. */
.header-contact-icon {
  display: none;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 50%;
  background-color: #13294b;
  /* "Contact us" composite (approx. of the provided graphic): a phone handset
     (lower-left, primary = tap to call) + an envelope (upper-right = email). */
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 512 512'%3E%3Cpath fill='%23ffffff' transform='translate(0 80) scale(0.6)' d='M164.9 24.6c-7.7-18.6-28-28.5-47.4-23.2l-88 24C12.1 30.2 0 46 0 64C0 311.4 200.6 512 448 512c18 0 33.8-12.1 38.6-29.5l24-88c5.3-19.4-4.6-39.7-23.2-47.4l-96-40c-16.3-6.8-35.2-2.1-46.3 11.6L304.7 368C234.3 334.7 177.3 277.7 144 207.3L193.3 167c13.7-11.1 18.4-30 11.6-46.3l-40-96z'/%3E%3Cpath fill='%23ffffff' transform='translate(255 45) scale(0.42)' d='M48 64C21.5 64 0 85.5 0 112c0 15.1 7.1 29.3 19.2 38.4L236.8 313.6c11.4 8.5 27 8.5 38.4 0L492.8 150.4C504.9 141.3 512 127.1 512 112c0-26.5-21.5-48-48-48H48zM0 176V384c0 35.3 28.7 64 64 64H448c35.3 0 64-28.7 64-64V176L294.4 339.2c-22.8 17.1-54 17.1-76.8 0L0 176z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: center;
  background-size: 22px 22px;
  padding: 0;
  cursor: pointer;
  vertical-align: middle;
}

/* ===== Contact modal: tap-to-call phone block ===== */
.hc-modal-phone {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border: 1px solid #d8dde3;
  border-radius: 8px;
  background: #f4f6f9;
  text-decoration: none !important;
  color: #13294b;
}
.hc-modal-phone:hover {
  background: #eef2f7;
}
.hc-modal-phone-ico {
  flex: 0 0 auto;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background-color: #c0392b;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 512 512' fill='%23ffffff'%3E%3Cpath d='M164.9 24.6c-7.7-18.6-28-28.5-47.4-23.2l-88 24C12.1 30.2 0 46 0 64C0 311.4 200.6 512 448 512c18 0 33.8-12.1 38.6-29.5l24-88c5.3-19.4-4.6-39.7-23.2-47.4l-96-40c-16.3-6.8-35.2-2.1-46.3 11.6L304.7 368C234.3 334.7 177.3 277.7 144 207.3L193.3 167c13.7-11.1 18.4-30 11.6-46.3l-40-96z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: center;
  background-size: 18px 18px;
}
.hc-modal-phone-text {
  display: flex;
  flex-direction: column;
}
.hc-modal-phone-num {
  font-size: 22px;
  font-weight: 700;
  color: #c0392b;
  line-height: 1.1;
}
.hc-modal-phone-sub {
  font-size: 12px;
  color: #6b7682;
}
.hc-modal-or {
  display: flex;
  align-items: center;
  text-align: center;
  color: #8a94a0;
  font-size: 12px;
  margin: 14px 0 10px;
}
.hc-modal-or::before,
.hc-modal-or::after {
  content: "";
  flex: 1 1 auto;
  border-top: 1px solid #e2e6ea;
}
.hc-modal-or span {
  padding: 0 10px;
}

.header-contact-line {
  font-size: 13px;
  color: #333;
  text-decoration: none;
  /* Bold for both phone and email. */
  font-weight: 700;
  white-space: nowrap;
}

.header-contact-line:hover {
  color: #c0392b;
  text-decoration: underline;
}

/* ===== PHONE LINE — red + larger =====
   The first .header-contact-line in the template is the
   phone number (the second is the email, which carries the
   .header-contact-email modifier and overrides this). The
   phone gets brand red plus a noticeably larger font so it
   reads as the primary contact channel — Steele Customer
   Service answers calls during business hours, and the
   email is the secondary fallback. The :not(.header-contact-
   email) qualifier keeps this rule from accidentally
   bleeding onto the email line when CSS source-order
   wouldn't catch it. */
.header-contact-line:not(.header-contact-email) {
  color: #c0392b;
  font-size: 18px;
  font-weight: 700;
}
.header-contact-line:not(.header-contact-email):hover {
  /* Slightly darker red on hover so the link feedback is
     still visible when the resting state is already red. */
  color: #a02b1f;
}

/* ===== EMAIL LINE — blue =====
   The user asked for parts@jcsteele.com in blue so it
   visually distinguishes from the red phone above. Standard
   anchor-blue (#1565c0) reads as a clickable link without
   competing against the brand red used elsewhere. */
.header-contact-email {
  font-size: 12px;
  color: #1565c0;
  font-weight: 600;
}
.header-contact-email:hover {
  /* Match the link convention — darker shade on hover for
     the email rather than flipping to brand red, which
     would look like a wrong-color jump. */
  color: #0d47a1;
}

/* Utility row (formerly combined cart / my account) — kept
   horizontal but tightened against the company selector above.
   Replaces the old .pt-3 that spaced it 1rem down. The View Cart
   cluster that previously occupied the left half of this row was
   removed when the CartWidget took over cart navigation, so the
   row now hosts only the My Account dropdown (right-aligned via
   the #header-account justify-content: flex-end rule below). */
.header-utility-row {
  margin-top: 4px;
  margin-left: 0;
  margin-right: 0;
  text-align: center; /* reset since .account-cont sets text-align: right */
}

/* The admin-only "Role:" line adds a third line to the identity stack. Inside
   the fixed-height (125px) header that pushes the in-flow My Account row down
   onto the nav bar. For admins (and only admins — the role line is gated on
   isRealAdmin) pull the My Account row back up by ~the role line's height so it
   sits just below the role line and clears the nav. Customers never get the
   role line, so .has-role-line is absent and their spacing is unchanged. */
.account-cont.has-role-line .user-greeting {
  line-height: 1.15;
}
.account-cont.has-role-line .company-selector {
  padding-top: 0;
  padding-bottom: 0;
}
.account-cont.has-role-line .company-selector-wrapper.company-selector-top {
  margin-bottom: 0;
}
.account-cont.has-role-line .header-utility-row {
  margin-top: -14px;
}

#header-account {
  display: flex;
  align-items: center;
  /* Right-align My Account within the now-full-width col-lg-12.
     Previously the column was col-lg-4 sitting next to the View
     Cart cluster (col-lg-8) so center-alignment looked right;
     after the cart cluster was removed, this column owns the
     whole row and needs to hug the right edge so My Account stays
     anchored to where users expect it (matching the line above
     it: "Hello {name}" / "JC Steele & Sons — (NC###)"). */
  justify-content: flex-end;
}

/* ===== ICONIFIED NAV LINK =====
   Match the visual treatment of the .dropdown-toggle (My
   Account trigger when logged in) for nav links that
   benefit from the same icon — currently just the
   logged-out "Login" link. Copy of the global SVG data
   URI used elsewhere in the codebase, kept here verbatim
   so removing or restyling .dropdown-toggle in the future
   doesn't silently change Login's appearance. If the
   master icon ever updates, update this URI to match. */
.nav-link-with-icon {
  display: inline-flex !important;
  align-items: center;
  gap: 6px;
}

.nav-link-with-icon::before {
  content: "";
  display: inline-block;
  width: 20px;
  height: 20px;
  background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20'%3E%3Cpath fill='%23ed1d24' d='M10 17.2a7.2 7.2 0 01-6-3.22c.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08a7.2 7.2 0 01-6 3.22zM10 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0-3C4.48 0 0 4.48 0 10s4.48 10 10 10 10-4.48 10-10S15.52 0 10 0z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
}

.expiry-countdown {
  font-size: 24px;
  font-weight: 700;
  color: #c0392b;
}

.expiry-buttons {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.expiry-buttons .btn {
  font-weight: 700;
  font-size: 14px;
  padding: 12px;
}

.saving-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.92);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
}

.saving-message {
  text-align: center;
}

.saving-message p {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin-top: 16px;
}

.saving-spinner {
  width: 40px;
  height: 40px;
  margin: 0 auto;
  border: 4px solid #ddd;
  border-top: 4px solid #c0392b;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* ===== FLOATING HEADER SEARCH =====
   Positioned over the breadcrumb row, right-aligned with the body
   content. The `right` value is set globally (with !important) in
   steele-consistency-fix.css so it tracks the same responsive
   breakpoints as the rest of the page (540 / 720 / 960 / 1140), and
   the `top` value is set inline by App.vue's applyStickyClasses() so
   the search stays vertically aligned with the breadcrumb regardless
   of sticky header height changes (login/logout, viewport reflows). */
#header-search {
  position: fixed;
  width: 360px;
  max-width: calc(100vw - 48px);
  z-index: 999; /* below sticky header (z 1000) so dropdowns from the
                   nav can still cover it if needed */
  /* `right` is set in steele-consistency-fix.css */
  /* `top` is set via inline style by App.vue */
}
/* Reporting-role header: date shortcuts + report filter search */
#header-search.is-reporting { width: 520px; }
.hdr-report-search { display: flex; align-items: center; gap: 6px; }
.hdr-daterange { display: flex; flex-wrap: wrap; gap: 4px; }
.hdr-dbtn { border: 1px solid #d4d4d8; background: #fff; color: #555; font-size: 11px; font-weight: 700; padding: 4px 8px; border-radius: 6px; cursor: pointer; line-height: 1.35; }
.hdr-dbtn:hover { background: #9e2436; color: #fff; border-color: #9e2436; }
/* Compact From/To date pickers (left of the shortcut buttons). Each is a white pill (matching the
   shortcut buttons) so its tiny label stays readable regardless of the header background / theme. */
.hdr-dpick { display: inline-flex; align-items: center; gap: 4px; border: 1px solid #d4d4d8; background: #fff; border-radius: 6px; padding: 2px 6px; }
.hdr-dpick:focus-within { border-color: #9e2436; }
.hdr-dpick label { font-size: 10px; font-weight: 700; color: #777; text-transform: uppercase; letter-spacing: .02em; margin: 0; }
.hdr-dinput { border: 0; background: transparent; color: #333; font-size: 11px; padding: 1px 0; line-height: 1.2; max-width: 116px; }
.hdr-dinput:focus { outline: none; }
.hdr-filtersearch { flex: 1 1 auto; min-width: 0; }

/* ===== TEST MODE TOGGLE =====
   Sits centered on the breadcrumb row when visible. Uses
   the same `position: fixed` strategy as #header-search,
   with `top` set inline by App.vue to track the dynamic
   sticky-header height (so vertical position adjusts as
   the header reflows on login/logout / company change /
   etc.). If App.vue doesn't yet set a top here, the
   fallback below puts it roughly on the breadcrumb row
   for the typical logged-in header height — adjust the
   App.vue inline-style writer to also target this id
   when reflowing.

   z-index 999 matches #header-search so dropdowns from
   the sticky header (z 1000) can still cover it. */
#test-mode-toggle-row {
  position: fixed;
  left: 50%;
  transform: translateX(-50%);
  top: 245px; /* fallback; App.vue may override inline */
  z-index: 999;
}

.test-mode-toggle-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 14px;
  font-family: var(--steele-font-medium, "Lato", "Helvetica Neue", Arial, sans-serif);
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  font-weight: 700;
  background: #000;
  color: #fff;
  border: 1px solid #000;
  border-radius: 4px;
  cursor: pointer;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
  transition: background-color 120ms ease,
              border-color 120ms ease;
}

.test-mode-toggle-btn:hover:not(:disabled) {
  background: #1a1a1a;
}

.test-mode-toggle-btn.active {
  background: #c0392b;
  border-color: #c0392b;
}

.test-mode-toggle-btn.active:hover:not(:disabled) {
  background: #a93226;
  border-color: #a93226;
}

.test-mode-toggle-btn:disabled {
  opacity: 0.6;
  cursor: wait;
}

/* Small status dot — solid when ON, hollow when OFF.
   Visual reinforcement of state beyond text alone. */
.test-mode-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  border: 2px solid #fff;
  background: transparent;
}

.test-mode-toggle-btn.active .test-mode-dot {
  background: #fff;
}

#header-search #search {
  margin: 0;
}

#header-search .search-input-container {
  position: relative;
  display: flex;
  align-items: center;
  background: #fff;
  border: 1px solid #d8d8d8;
  border-radius: 6px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}

#header-search .search-input-container:focus-within {
  border-color: #c0392b;
  box-shadow: 0 1px 4px rgba(192, 57, 43, 0.15);
}

#header-search input#input-with-list {
  border: none !important;
  background: transparent !important;
  height: 36px;
  padding: 0 34px 0 38px; /* right room for the × clear button */
  font-size: 13px;
  width: 100%;
}

#header-search .search-clear-btn {
  position: absolute;
  right: 4px;
  top: 0;
  bottom: 0;
  width: 28px;
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 20px;
  line-height: 1;
  color: #999 !important;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}
#header-search .search-clear-btn:hover {
  color: #c0392b !important;
}

#header-search input#input-with-list:focus {
  outline: none;
  box-shadow: none;
}

#header-search .search-btn {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 38px;
  border: none;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

#header-search .search-btn span {
  display: inline-block;
  width: 16px;
  height: 16px;
  background: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="%23c0392b" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>') no-repeat center;
  background-size: contain;
}

#header-search .custom-dropdown {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: #fff;
  border: 1px solid #d8d8d8;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  max-height: 320px;
  overflow-y: auto;
  z-index: 1000;
}

@media (max-width: 767.98px) {
  #header-search {
    /* At narrow viewports the global responsive `right` calc would
       compute negative (viewport < 540px), pushing the search off
       screen. Override here with a small fixed offset. !important
       required to outrank the global !important rule when both have
       the same specificity-from-scoping. */
    right: 12px !important;
    width: 220px;
  }
}

@media (max-width: 575.98px) {
  /* On very narrow viewports the breadcrumb row gets cramped.
     Hide the floating search and let users tap a dedicated mobile
     search affordance if needed (or surface it elsewhere). */
  #header-search {
    display: none;
  }
}

/* ===== MOBILE HEADER — ONE compact row (phones, <768px) =====
   responsive.js no longer reparents the logo / search / account into the nav
   (the nav is an inline menu bar now), so they live here. ONE middle-aligned
   row, left → right: small logo · phone · email · company icon · account icon
   (user spec, b25 — collapsed back from the b24 two-row split now that the
   company selector is icon-only and the whole set fits a single line). The
   greeting is dropped. body padding-top auto-tracks the header height. */
@media (max-width: 767.98px) {
  /* Compact header — trim the vertical padding so there isn't a tall empty
     band below the single icon row (user request). */
  #header-wrapper {
    /* The global desktop rule pins #header-wrapper to height:125px, leaving
       ~79px of dead space below the compact mobile icon row (and pushing the
       overlay anchor + page content way down). Let it size to its content. */
    height: auto !important;
    padding-top: 3px !important;
    padding-bottom: 3px !important;
    min-height: 0 !important;
  }
  #header-wrapper .container {
    padding-left: 8px;
    padding-right: 8px;
    padding-top: 0;
    padding-bottom: 0;
  }
  #header-wrapper .row.align-items-start {
    /* !important throughout: Bootstrap's .align-items-start utility AND the
       global steele-consistency-fix.css header rules all use !important, so
       plain declarations were silently losing (top-aligned row, wrong wrap,
       and — the b26 holdout — the logo dropping to its own line, which means
       direction/order was being forced elsewhere). Pin ALL of it. */
    display: flex !important;
    flex-direction: row !important;     /* not column/column-reverse */
    flex-wrap: nowrap !important;        /* one line, no wrapping the logo down */
    align-items: center !important;      /* middle-align logo / contact / icons */
    justify-content: flex-start !important;
    gap: 0;
    /* Kill Bootstrap's .row -15px side margins — they overflowed past the
       container and CLIPPED the left edge ("(800)" rendered as "800)"). */
    margin-left: 0 !important;
    margin-right: 0 !important;
    /* Right padding so the account icon's distance from the right edge matches
       the logo's from the left: 8px container pad + 6px here = 14px, same as
       8px container pad + 6px logo margin-left on the left. */
    padding-right: 6px;
  }
  /* All three columns size to content, sit inline, and keep an EXPLICIT order
     (logo → contact → icons) so no stray `order` rule can reshuffle them. */
  #header-wrapper .logo-cont,
  #header-wrapper .contact-cont,
  #header-wrapper .account-cont {
    width: auto !important;
    max-width: none !important;
    padding: 0 !important;
  }
  /* logo-cont GROWS to fill the space left of the icon group and centers the
     logo within it → the logo sits centered between the left edge and the
     contact icon (user request). */
  #header-wrapper .logo-cont {
    order: 0 !important;
    flex: 1 1 auto !important;
    text-align: center !important;
    margin: 0 !important;
  }
  /* The 3 icons group on the right; the logo's growth pushes them there.
     account-cont sits 8px after the contact icon; the rightmost icon lands 14px
     from the edge (8px container pad + 6px row padding-right). */
  #header-wrapper .contact-cont { order: 1 !important; flex: 0 0 auto !important; margin: 0 !important; }
  #header-wrapper .account-cont { order: 2 !important; flex: 0 0 auto !important; margin: 0 0 0 8px !important; }
  /* Small logo — override the 220px desktop logo and its 30px shift. Nudge it
     slightly RIGHT and UP (user request, b25). */
  .logo-cont {
    margin-top: 0;
    margin-left: 6px;             /* move right a little */
    overflow: visible;
  }
  /* THE root-cause fix (confirmed via the vw=390 diagnostic): the original
     site CSS sets `#logo { position:absolute; left:40%; top:0; height:100% }`
     at all widths. Absolute → out of flow → .logo-cont collapses to w=0 and
     the logo floats over the phone/email. Force it back into normal flow so
     it sits inline in the row. */
  #header-wrapper .logo-cont #logo {
    position: static !important;
    float: none !important;
    left: auto !important;
    top: auto !important;
    right: auto !important;
    height: auto !important;
    max-height: none !important;
    padding: 0 !important;
    transform: none !important;  /* center with the row (the -3px nudge sat too high) */
    display: inline-block !important;
  }
  #header-wrapper .logo-cont #logo img {
    width: 84px !important;   /* bigger (user request); 2.19:1 → ~38px tall */
    height: auto !important;
    max-width: none !important;
  }
  /* Defensive: only the light logo ever shows in the normal (light) header.
     The dark-mode <img> is display:none via darkmode.css, but force it here
     too so a stray theme/specificity edge can never double the logo. */
  .logo-cont .site-logo-dark {
    display: none !important;
  }
  /* Phone + email inline, just right of the logo and vertically centered with
     it (user: "phone/email come down a little to be middle-aligned with the
     logo"). */
  .contact-cont {
    padding: 0;
    margin-left: 12px;
  }
  /* Phone/email TEXT is replaced by the tappable contact icon on phones. */
  .header-contact {
    display: none !important;
  }
  .header-contact-icon {
    display: inline-block;
  }
  .header-contact-line {
    font-size: 11px;
    line-height: 1.25;
  }
  .header-contact-line:not(.header-contact-email) {
    font-size: 11px;   /* phone smaller (was 13px) — user request */
  }
  /* Drop the greeting — the company selector shows the company name. */
  .user-greeting {
    display: none;
  }
  /* The two ICONS (company selector + My Account) sit inline, just right of
     the email and vertically centered (user spec). The global
     steele-consistency-fix.css forces #header-wrapper .account-cont to
     flex-direction:column (desktop stacking) at ALL widths — override to row
     with matching specificity + !important. (margin/order handled above.) */
  #header-wrapper .account-cont {
    display: flex !important;
    flex-direction: row !important;
    align-items: center !important;
    justify-content: flex-start !important;
    gap: 8px;                 /* even spacing between company + account icons */
    text-align: left;
    padding-right: 0 !important;
  }
  /* Reset the global company-selector-wrapper vertical margins on mobile. */
  #header-wrapper .company-selector-wrapper {
    margin-top: 0 !important;
    margin-bottom: 0 !important;
  }
  .account-identity {
    display: flex;
    align-items: center;
    margin: 0;
  }
  .company-selector-wrapper.company-selector-top {
    margin: 0;
  }
  .company-selector {
    display: inline-flex;
    align-items: center;
    padding: 0;
  }
  /* ICONS ONLY (user request): hide the company NAME + the dropdown caret in
     the trigger — the gray buildings circle is the whole affordance. The
     selected company name still appears as the first line of the dropdown
     (.company-dropdown-selected) when tapped. */
  .company-selector-label,
  .company-selector-arrow {
    display: none !important;
  }
  /* Gray circle + white buildings icon = the company button. Slightly larger
     since it now stands alone (no name beside it), no trailing margin. */
  .company-selector::before {
    content: "";
    flex: 0 0 auto;
    width: 32px;
    height: 32px;
    margin-right: 0;
    border-radius: 50%;
    background-color: #888;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%23ffffff'%3E%3Cpath d='M3 21V9l5-2v14H3zm6 0V6l6-3v18H9zm7 0V8l5 2v11h-5z'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: center;
    background-size: 16px 16px;
    vertical-align: middle;
  }
  /* My Account → compact red person icon (hide the "My Account" text),
     part of the CENTERED row-2 icon pair (no margin-auto push). The
     .header-utility-row is a Bootstrap .row (negative side margins) so we
     reset them and let it size to the icon. */
  .header-utility-row {
    margin: 0 !important;
    width: auto;
    min-height: 0;
    flex: 0 0 auto;
  }
  /* Same story as #logo: the original CSS sets
     `#header-account { position:absolute; right:0; top:30px }`, which collapses
     the utility row to w=0 and floats the account icon. Force it in-flow so it
     sits inline as the right-hand icon of the pair. */
  #header-wrapper #header-account {
    position: static !important;
    right: auto !important;
    top: auto !important;
    transform: none !important;
    margin: 0 !important;
    padding: 0 !important;
    width: auto !important;
    flex: 0 0 auto !important;
  }
  /* Desktop form-embed chrome (FormStack placeholders) — not part of the
     mobile header; drop it so it can't disturb the row-2 flex layout. */
  .header-tools {
    display: none;
  }
  #header-account .nav-link {
    padding: 2px 4px;
    font-size: 0;
  }
  #header-account .nav-link::before {
    content: "";
    display: inline-block;
    width: 32px !important;          /* match the contact + company icons */
    height: 32px !important;
    border-radius: 50%;
    background-color: #c0392b;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 448 512' fill='%23ffffff'%3E%3Cpath d='M224 256A128 128 0 1 0 224 0a128 128 0 1 0 0 256zm-45.7 48C79.8 304 0 383.8 0 482.3 0 498.7 13.3 512 29.7 512l388.6 0c16.4 0 29.7-13.3 29.7-29.7C448 383.8 368.2 304 269.7 304l-91.4 0z'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: center;
    background-size: 16px 16px;
    /* Reset the OLD global avatar rule's offsets (margin-right:8px / top:6px /
       position:relative) that were shifting this icon down + right, making it
       look mismatched against the contact + company icons. */
    margin: 0 !important;
    top: 0 !important;
    position: static !important;
    vertical-align: middle;
  }
  #header-account .dropdown-toggle::after {
    display: none;
  }

  /* ---- Company dropdown: CENTER it on the page (user request) ----
     The desktop variant anchors right:0 to the trigger; now that the trigger
     is a small icon, that pushed the menu off the LEFT edge (the user's
     screenshot). Pin it fixed + horizontally centered in the viewport instead,
     dropping just below the single header row (~52px). The first selector
     matches the .company-selector-top variant's specificity (and is later in
     source) so it wins; the second covers the bare class. */
  .company-selector-wrapper.company-selector-top .company-dropdown,
  .company-dropdown {
    position: fixed;
    /* Drop right below the logo/icons row (the user's "orange line"), covering
       the nav + content — NOT below the whole sticky header. */
    top: var(--jcs-header-bottom, 90px);
    left: 50%;
    right: auto;
    transform: translateX(-50%);
    width: 92vw;
    max-width: 360px;
    min-width: 0;
    max-height: calc(100vh - var(--jcs-header-bottom, 90px) - 12px);
    z-index: 9000;
  }
  /* Reveal the selected-company line as the dropdown's first row (icon-only
     trigger means the menu needs to confirm the current pick at the top). */
  .company-dropdown-selected {
    display: block;
  }

  /* ---- My Account dropdown: HOVER OVER the nav menu + go compact ----
     (user: "the drop down needs to hover over the menu" + "smaller to fit all
     the menu options"). It's a Bootstrap dropdown — Popper positions it with
     inline position:absolute + transform and it was getting painted UNDER the
     nav band / cart bar. Force it fixed just below the header row, pinned near
     the right edge under its icon, with a z-index above the nav + cart bar, and
     tighten padding/font so Account Information · Admin Dashboard · Logout all
     fit without clipping. !important defeats Popper's inline styles. */
  #header-account .dropdown-menu {
    position: fixed !important;
    top: var(--jcs-header-bottom, 90px) !important;  /* below the logo/icons row */
    right: 8px !important;
    left: auto !important;
    transform: none !important;
    z-index: 9000 !important;
    min-width: 190px;
    margin: 0;
    padding: 4px 0;
    font-size: 13px;
    border: 1px solid #d8dde3;
    box-shadow: 0 8px 22px rgba(0, 0, 0, 0.22);
  }
  #header-account .dropdown-item {
    padding: 7px 16px;
    font-size: 13px;
  }
  #header-account .dropdown-divider {
    margin: 3px 0;
  }
}

/* All Google Translate widget styling lives in
   steele-consistency-fix.css under the .util-translate-content
   selectors. Consolidating there avoids the rule-fighting we
   ran into when styling was split between this file and the
   global stylesheet. */

.message {
  font-size: small;
}

.radio {
  padding-top: 2px;
}

#input-with-list {
  height: 45.7px;
}

.search-input-container {
  position: relative;
  display: flex;
  align-items: center;
}

.custom-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 10;
  background: white;
  border: 1px solid #ddd;
  border-top: none;
  border-radius: 0 0 4px 4px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  max-height: 200px;
  overflow-y: auto;
}

.dropdown-option {
  padding: 10px 15px;
  cursor: pointer;
  transition: background-color 0.2s;
  display: flex;
  flex-direction: column;
  border-bottom: 1px solid #f0f0f0;
}

.dropdown-option:last-child {
  border-bottom: none;
}

.dropdown-option:hover {
  background-color: #f5f5f5;
}

.option-code {
  font-weight: 600;
  color: #333;
}

.option-detail {
  font-size: 12px;
  color: #888;
  margin-top: 2px;
}

#search input[type="text"] {
  width: 100%;
  padding: 10px 40px 10px 45px;
  border: 1px solid #ddd;
  font-size: 14px;
  transition: border-color 0.3s, box-shadow 0.3s;
}
</style>

<!-- UNSCOPED: the contact modal's .modal-dialog wrapper is rendered by
     bootstrap-vue at <body> level (teleported), so it does NOT carry Head's
     scoped data-v attribute — its positioning rule must be global. On mobile,
     drop the modal from the bottom of the header chrome (same anchor as the
     dropdowns) instead of centering it over the header. -->
<style>
/* Z-INDEX: the bootstrap modal default (1050) was losing to the sticky header
   because Semantic UI (loaded for the dropdowns) ALSO defines `.modal` with a
   conflicting z-index (1001 / -1), dropping our modal below the sticky header
   so the nav rendered IN FRONT of it ("behind the top menu"). Force it above
   everything, like the 9000 dropdowns. The backdrop (1040) already covers the
   sticky header (z-index 1000), so it stays dimmed beneath the panel. */
.hc-contact-modal {
  z-index: 9600 !important;
}
@media (max-width: 767.98px) {
  /* Pin the contact dialog at the header bottom (the user's "orange line"),
     like the dropdowns. NOTE: the class goes on .modal via `modal-class`
     (dialog-class didn't land here), so we target the descendant .modal-dialog
     — matching the app's existing .modify-order-modal .modal-dialog pattern.
     Narrower than full-width per request; content scrolls if the form is tall. */
  .hc-contact-modal .modal-dialog {
    position: fixed !important;
    top: var(--jcs-header-bottom, 90px) !important;
    left: 50% !important;
    transform: translateX(-50%) !important;
    margin: 0 !important;
    width: 88vw !important;
    max-width: 420px !important;
  }
  .hc-contact-modal .modal-content {
    max-height: calc(100vh - var(--jcs-header-bottom, 90px) - 14px) !important;
    overflow-y: auto !important;
  }
  /* The dialog top is ALREADY at --jcs-header-bottom (verified === top:92px,
     the same line as the dropdowns). The bootstrap title bar was the only thing
     making the CONTENT look lower, so hide it — the "(800) 278-3353 / Tap to
     call" phone block already reads as the modal's header. Close via Cancel /
     backdrop tap. */
  .hc-contact-modal .modal-header {
    display: none !important;
  }
  .hc-contact-modal .modal-body {
    padding-top: 14px !important;
  }
}
</style>